// ParentPortal — separate file to keep App.jsx manageable
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMessages, sendMessage, studentRegisterEmail, studentRequestRegisterOtp, apiParentLogin, parentVerifyPassword, studentRequestChangeEmailOtp, studentVerifyCurrentEmailOtp, studentConfirmNewEmail, getStudentProfile, parentChangePassword, uploadMyDocument, getMyDocuments, getMyDocument } from './storage';
import { buildCard, buildCardBack, CARD_DIMS } from './print';
import { buildRCHtmlForStudent } from './ReportCardStudio';

const PARENT_NAV = [
  { id:'pdash',    icon:'home',          label:'Dashboard'   },
  { id:'pfee',     icon:'payments',      label:'Fee Status'  },
  { id:'patt',     icon:'fact_check',    label:'Attendance'  },
  { id:'pmarks',   icon:'quiz',          label:'Exams & Marks' },
  { id:'pcal',     icon:'calendar_month',label:'Calendar'    },
  { id:'pmsg',     icon:'mail',          label:'Messages'    },
  { id:'pidcard',  icon:'badge',         label:'ID Card'     },
  { id:'prc',      icon:'description',   label:'Report Card' },
  { id:'pteachers',icon:'school',        label:'My Teachers' },
  { id:'pdocs',    icon:'folder_open',   label:'Documents'   },
  { id:'pprofile', icon:'person',        label:'Profile'     },
];

const IDLE_TIMEOUT = 10 * 60 * 1000; // 10 min → lock
const WARN_BEFORE  =  2 * 60 * 1000; // warn 2 min before

export default function ParentPortal({ db, student, activeSessionId, onLogout }) {
  const [page, setPage] = useState('pdash');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(() => {
    if (student.email || student.em) return false;
    // Don't re-show if dismissed this session
    return !sessionStorage.getItem('lkps_email_prompt_dismissed_' + (student.sid || student.id));
  });
  const [emailInput, setEmailInput] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailStep, setEmailStep] = useState('input');
  const [emailErr, setEmailErr] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  const sideRef = React.useRef(null);
  const sideOpen = React.useRef(false);

  const expandSide = () => {
    if (sideOpen.current) return;
    sideOpen.current = true;
    const el = sideRef.current; if (!el) return;
    el.style.width = '230px';
    el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity='1'; n.style.maxWidth='180px'; });
    el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity='1'; n.style.maxWidth='160px'; });
    el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent='flex-start'; n.style.padding='10px 12px'; });
  };

  const collapseSide = () => {
    if (!sideOpen.current) return;
    sideOpen.current = false;
    const el = sideRef.current; if (!el) return;
    el.style.width = '60px';
    el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity='0'; n.style.maxWidth='0'; });
    el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity='0'; n.style.maxWidth='0'; });
    el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent='center'; n.style.padding='10px 0'; });
  };

  // Init collapsed state on desktop
  React.useEffect(() => {
    if (!isMobile) collapseSide();
    else {
      const el = sideRef.current; if (!el) return;
      el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity='1'; n.style.maxWidth='180px'; });
      el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity='1'; n.style.maxWidth='160px'; });
      el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent='flex-start'; n.style.padding='10px 12px'; });
      sideOpen.current = true;
    }
  }, [isMobile]);
  const [locked, setLocked] = useState(false);
  const [lockWarning, setLockWarning] = useState(false);
  const [lockPw, setLockPw] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  useEffect(() => {
    const h = e => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const idleTimer = useRef(null);
  const warnTimer = useRef(null);

  const clearTimers = () => { clearTimeout(idleTimer.current); clearTimeout(warnTimer.current); };

  const resetIdleTimer = useCallback(() => {
    if (locked) return;
    clearTimers();
    setLockWarning(false);
    warnTimer.current = setTimeout(() => setLockWarning(true), IDLE_TIMEOUT - WARN_BEFORE);
    idleTimer.current = setTimeout(() => {
      setLocked(true);
      setLockWarning(false);
      // Clear localStorage so reload forces fresh login instead of auto-restoring
      try {
        localStorage.removeItem('lkps_user');
        localStorage.removeItem('lkps_user_pic');
        localStorage.removeItem('lkps_active_session');
      } catch(e) {}
    }, IDLE_TIMEOUT);
  }, [locked]);

  useEffect(() => {
    const events = ['mousemove','keydown','mousedown','touchstart','scroll','click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => { events.forEach(e => window.removeEventListener(e, resetIdleTimer)); clearTimers(); };
  }, [resetIdleTimer]);

  const tryUnlock = async () => {
    if (!lockPw) { setLockErr('Enter your password'); return; }
    // child record has puser (username) — use it to re-authenticate
    const s = db.students.find(x => (x.sid||x.id) === student.sid);
    const puser = s?.puser || student.puser || '';
    if (!puser) { setLockErr('Cannot verify — contact school office'); return; }
    try {
      await apiParentLogin(puser, lockPw);
      setLocked(false); setLockPw(''); setLockErr(''); resetIdleTimer();
    } catch(e) {
      setLockErr('Incorrect password'); setLockPw('');
    }
  };

  const name = (student.fn || '') + ' ' + (student.ln || '');
  const [livePhoto, setLivePhoto] = useState(student.photo || '');
  const photo = livePhoto || (db.photos||{})[student.sid] || '';
  const ini = name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // Find child in db, merge live photo
  const childBase = db.students.find(s => (s.sid || s.id) === student.sid) || student;
  const child = livePhoto ? { ...childBase, photo: livePhoto } : childBase;

  // Refresh photo from server (picks up approved passport photo)
  useEffect(() => {
    getStudentProfile()
      .then(r => { if (r.student?.photo) setLivePhoto(r.student.photo); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sendRegisterOtp = async () => {
    if (!emailInput || !emailInput.includes('@')) { setEmailErr('Enter a valid email address'); return; }
    setEmailSaving(true); setEmailErr('');
    try {
      await studentRequestRegisterOtp(emailInput);
      setEmailStep('otp');
    } catch(e) { setEmailErr(e.message || 'Failed to send OTP. Please try again.'); }
    finally { setEmailSaving(false); }
  };

  const saveEmail = async () => {
    if (!emailOtp) { setEmailErr('Enter the OTP'); return; }
    setEmailSaving(true); setEmailErr('');
    try {
      await studentRegisterEmail(emailInput, emailOtp);
      student.email = emailInput;
      student.em = emailInput;
      // Persist to localStorage so reload doesn't re-show the prompt
      try {
        const stored = JSON.parse(localStorage.getItem('lkps_user') || '{}');
        stored.email = emailInput;
        stored.em = emailInput;
        localStorage.setItem('lkps_user', JSON.stringify(stored));
      } catch(_) {}
      setShowEmailPrompt(false);
    } catch(e) {
      setEmailErr(e.message || 'Invalid OTP — please re-enter your email');
      setEmailOtp('');
      setEmailInput('');
      setEmailStep('input');
    }
    finally { setEmailSaving(false); }
  };

  useEffect(() => {
    if (isMobile) setMobileNavOpen(false);
  }, [page, isMobile]);

  // Lock screen
  if (locked) return (
    <div style={{position:'fixed',inset:0,background:'linear-gradient(145deg,#001530,#002045)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999,fontFamily:'Inter,sans-serif'}}>
      <div style={{width:'100%',maxWidth:340,padding:'32px 28px',borderRadius:20,background:'rgba(255,255,255,0.06)',border:'1.5px solid rgba(255,255,255,0.12)',backdropFilter:'blur(20px)',boxShadow:'0 24px 60px rgba(0,0,0,0.5)',textAlign:'center'}}>
        <div style={{width:56,height:56,borderRadius:'50%',background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 14px',boxShadow:'0 8px 24px rgba(25,96,163,0.4)'}}>
          <span className="material-symbols-outlined" style={{fontSize:26,color:'#fff',fontVariationSettings:"'FILL' 1"}}>lock</span>
        </div>
        <div style={{fontSize:17,fontWeight:800,color:'#fff',marginBottom:4}}>Session Locked</div>
        <div style={{fontSize:12,color:'rgba(255,255,255,0.45)',marginBottom:6}}>Locked due to inactivity</div>
        <div style={{fontSize:12,fontWeight:700,color:'rgba(255,255,255,0.55)',marginBottom:16}}>{name}</div>
        {lockErr && <div style={{background:'rgba(239,68,68,0.15)',color:'#fca5a5',borderRadius:10,padding:'8px 12px',fontSize:12,fontWeight:600,marginBottom:12,border:'1px solid rgba(239,68,68,0.3)'}}>{lockErr}</div>}
        <input value={lockPw} onChange={e=>{setLockPw(e.target.value);setLockErr('');}} type="password"
          placeholder="Enter your password" onKeyDown={e=>e.key==='Enter'&&tryUnlock()} autoFocus
          style={{width:'100%',padding:'12px 14px',borderRadius:12,border:'1.5px solid rgba(255,255,255,0.15)',background:'rgba(255,255,255,0.07)',fontSize:13,color:'#fff',outline:'none',boxSizing:'border-box',marginBottom:12}}/>
        <button onClick={tryUnlock} style={{width:'100%',padding:'12px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:800,fontSize:14,cursor:'pointer',marginBottom:10}}>
          Unlock
        </button>
        <button onClick={onLogout} style={{width:'100%',padding:'9px',borderRadius:12,border:'1.5px solid rgba(255,255,255,0.12)',background:'transparent',color:'rgba(255,255,255,0.45)',fontWeight:600,fontSize:12,cursor:'pointer'}}>
          Sign out instead
        </button>
      </div>
    </div>
  );

  const pages = {
    pdash:    <ParentDash db={db} child={child} student={student} setPage={setPage} />,
    pfee:     <ParentFee db={db} child={child} />,
    patt:     <ParentAttendance db={db} child={child} />,
    pmarks:   <ParentExams db={db} child={child} />,
    pexam:    <ParentExams db={db} child={child} />,
    pcal:     <ParentCalendar db={db} child={child} activeSessionId={activeSessionId} />,
    pmsg:     <ParentMessages db={db} child={child} student={student} activeSessionId={activeSessionId} />,
    pidcard:   <ParentIDCard db={db} child={child} />,
    prc:       <ParentReportCard db={db} child={child} />,
    pteachers: <ParentTeachers db={db} child={child} />,
    pdocs:     <ParentDocumentsPage child={child} activeSessionId={activeSessionId} />,
    pprofile:  <ParentProfile child={child} db={db} activeSessionId={activeSessionId} />,
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f0f4ff', fontFamily: 'Inter,sans-serif', overflow: 'hidden' }}>
      <style>{`
        @keyframes pp-slidein{from{opacity:0;transform:translateX(-100%)}to{opacity:1;transform:none}}
        @keyframes pp-fadein{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pp-pulse{0%,100%{box-shadow:0 0 0 0 rgba(25,96,163,0.25)}50%{box-shadow:0 0 0 6px rgba(25,96,163,0)}}
        @keyframes pp-bar-grow{from{height:0}to{height:var(--bar-h)}}
        @keyframes pp-progress{from{stroke-dashoffset:var(--dash-total)}to{stroke-dashoffset:var(--dash-offset)}}
        .pp-nav-btn:hover{background:rgba(96,165,250,0.15)!important;color:#60a5fa!important}
        .pp-nav-btn.active{background:rgba(96,165,250,0.18)!important;color:#60a5fa!important}
        .pp-stat{transition:all 200ms cubic-bezier(0.4,0,0.2,1)!important}
        .pp-stat:hover{transform:translateY(-4px)!important;box-shadow:0 12px 32px rgba(0,31,77,0.14)!important}
        .pp-card{transition:all 200ms cubic-bezier(0.4,0,0.2,1)!important}
        .pp-card:hover{box-shadow:0 8px 28px rgba(0,31,77,0.11)!important}
        .pp-fadein{animation:pp-fadein 0.45s ease both}
        .pp-pulse-badge{animation:pp-pulse 2s infinite}
        @media(max-width:768px){
          .pp-main-pad{padding:14px 12px!important}
          .pp-stats-grid{grid-template-columns:1fr 1fr!important;gap:10px!important}
          .pp-dash-grid{grid-template-columns:1fr!important}
          .pp-header-title{display:none!important}
          .pp-fee-timeline{grid-template-columns:repeat(4,1fr)!important}
          .pp-welcome-banner{flex-direction:column!important;padding:20px 18px!important}
          .pp-welcome-chart{display:none!important}
          .pp-welcome-fee{width:100%!important;min-width:unset!important}
          .pp-section-title{font-size:22px!important}
          .pp-table-wrap{font-size:11px!important}
          .pp-table-wrap th,.pp-table-wrap td{padding:8px 10px!important}
          .pp-card{border-radius:12px!important}
          .pp-stat{border-radius:12px!important;padding:14px!important}
          .pp-timeline-grid{grid-template-columns:repeat(4,1fr)!important;gap:6px!important}
          .pp-bar-chart{display:none!important}
        }
        @media(max-width:480px){
          .pp-stats-grid{grid-template-columns:1fr 1fr!important}
          .pp-fee-timeline{grid-template-columns:repeat(3,1fr)!important}
          .pp-timeline-grid{grid-template-columns:repeat(3,1fr)!important}
        }
      `}</style>

      {/* Idle warning banner */}
      {lockWarning && (
        <div style={{position:'fixed',top:0,left:0,right:0,zIndex:9998,background:'#b45309',color:'#fff',padding:'10px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:13,fontWeight:600,boxShadow:'0 2px 12px rgba(0,0,0,0.3)'}}>
          <span>⚠️ You'll be locked out in 2 minutes due to inactivity.</span>
          <button onClick={resetIdleTimer} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',padding:'5px 14px',borderRadius:8,cursor:'pointer',fontWeight:700,fontSize:12}}>Stay Active</button>
        </div>
      )}

      {/* Email Registration Prompt Modal */}
      {showEmailPrompt && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{width:'100%',maxWidth:400,background:'#fff',borderRadius:20,padding:'32px 28px',boxShadow:'0 24px 60px rgba(0,0,0,0.25)'}}>
            <div style={{width:48,height:48,borderRadius:14,background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:16}}>
              <span className="material-symbols-outlined" style={{fontSize:24,color:'#fff',fontVariationSettings:"'FILL' 1"}}>mail</span>
            </div>
            <div style={{fontSize:18,fontWeight:800,color:'#0f172a',marginBottom:6}}>Register Your Email</div>
            <div style={{fontSize:13,color:'#64748b',marginBottom:20,lineHeight:1.6}}>
              Register your email to enable self-service credential recovery if you forget your username or password.
            </div>
            {emailErr && <div style={{background:'#fef2f2',color:'#dc2626',borderRadius:10,padding:'9px 12px',fontSize:12,fontWeight:600,marginBottom:12,border:'1px solid #fecaca'}}>{emailErr}</div>}
            {emailStep === 'input' ? <>
              <input value={emailInput} onChange={e=>{setEmailInput(e.target.value);setEmailErr('');}}
                type="email" placeholder="your@email.com"
                style={{width:'100%',padding:'12px 14px',borderRadius:12,border:'1.5px solid #e2e8f0',background:'#f8fafc',fontSize:13,color:'#0f172a',outline:'none',boxSizing:'border-box',marginBottom:12}}
                onKeyDown={e=>e.key==='Enter'&&sendRegisterOtp()}/>
              <button onClick={sendRegisterOtp} disabled={emailSaving}
                style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:800,fontSize:14,cursor:'pointer',marginBottom:8}}>
                {emailSaving ? 'Sending OTP…' : 'Send OTP to Verify'}
              </button>
            </> : <>
              <div style={{fontSize:12,color:'#0369a1',fontWeight:600,marginBottom:8}}>OTP sent to <b>{emailInput}</b></div>
              <input value={emailOtp} onChange={e=>{setEmailOtp(e.target.value);setEmailErr('');}}
                placeholder="Enter 6-digit OTP" maxLength={6}
                style={{width:'100%',padding:'12px 14px',borderRadius:12,border:'1.5px solid #e2e8f0',background:'#f8fafc',fontSize:13,color:'#0f172a',outline:'none',boxSizing:'border-box',marginBottom:12}}
                onKeyDown={e=>e.key==='Enter'&&saveEmail()}/>
              <button onClick={saveEmail} disabled={emailSaving}
                style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:800,fontSize:14,cursor:'pointer',marginBottom:8}}>
                {emailSaving ? 'Saving…' : 'Verify & Register Email'}
              </button>
              <button onClick={()=>{setEmailStep('input');setEmailOtp('');setEmailErr('');}} style={{width:'100%',padding:'8px',borderRadius:12,border:'none',background:'transparent',color:'#64748b',fontSize:12,cursor:'pointer',marginBottom:4}}>
                Change email
              </button>
            </>}
            <button onClick={()=>{
              sessionStorage.setItem('lkps_email_prompt_dismissed_' + (student.sid || student.id), '1');
              setShowEmailPrompt(false);
            }}
              style={{width:'100%',padding:'10px',borderRadius:12,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:600,fontSize:12,cursor:'pointer'}}>
              Do it Later
            </button>
          </div>
        </div>
      )}

      {/* Sidebar overlay */}
      {/* Mobile overlay */}
      {isMobile && mobileNavOpen && (
        <div onClick={() => setMobileNavOpen(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1100,backdropFilter:'blur(2px)'}} />
      )}

      {/* Sidebar — collapsed icon-only on desktop, hover to expand; drawer on mobile */}
      <aside ref={sideRef}
        onMouseEnter={isMobile ? undefined : expandSide}
        onMouseLeave={isMobile ? undefined : collapseSide}
        style={{
          width: isMobile ? 230 : 60, flexShrink:0,
          background:'linear-gradient(180deg,#001530 0%,#002045 100%)',
          display:'flex', flexDirection:'column', overflow:'hidden',
          position: isMobile ? 'fixed' : 'relative',
          left:0, top: isMobile ? 0 : undefined, bottom: isMobile ? 0 : undefined,
          transform: isMobile ? (mobileNavOpen ? 'translateX(0)' : 'translateX(-110%)') : 'none',
          transition: isMobile ? 'transform 220ms ease' : 'width 260ms cubic-bezier(0.4,0,0.2,1)',
          zIndex: isMobile ? 1200 : 50,
          boxShadow:'4px 0 20px rgba(0,20,60,0.18)',
        }}>

        {/* Student avatar — clickable → profile */}
        <div onClick={()=>{setPage('pprofile');if(isMobile)setMobileNavOpen(false);}}
          style={{padding:'12px 0',borderBottom:'1px solid rgba(255,255,255,0.07)',display:'flex',alignItems:'center',gap:10,paddingLeft:isMobile?18:0,justifyContent:isMobile?'flex-start':'center',overflow:'hidden',cursor:'pointer'}}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(96,165,250,0.08)'}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          <div style={{width:32,height:32,borderRadius:'50%',background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:'#fff',flexShrink:0,overflow:'hidden'}}>
            {photo ? <img src={photo} alt={name} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : ini}
          </div>
          <div className="sb-user" style={{opacity:0,maxWidth:0,overflow:'hidden',transition:'opacity 200ms,max-width 260ms',whiteSpace:'nowrap',minWidth:0}}>
            <div style={{fontSize:11,fontWeight:700,color:'#fff',overflow:'hidden',textOverflow:'ellipsis'}}>{name}</div>
            <div style={{fontSize:9,color:'rgba(255,255,255,0.4)'}}>Class {child.cls||'—'}</div>
          </div>
        </div>
        {/* Nav */}
        <nav style={{flex:1,padding:'8px 8px',overflowY:'auto'}}>
          {PARENT_NAV.map(item => (
            <button key={item.id} onClick={()=>{setPage(item.id);if(isMobile)setMobileNavOpen(false);}}
              className="sb-btn"
              style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'10px 0',borderRadius:10,border:'none',cursor:'pointer',marginBottom:2,fontWeight:600,fontSize:13,transition:'all 150ms',textAlign:'left',justifyContent:'center',
                background: page===item.id ? 'rgba(96,165,250,0.18)' : 'transparent',
                color: page===item.id ? '#60a5fa' : 'rgba(255,255,255,0.55)'}}>
              <span className="material-symbols-outlined" style={{fontSize:20,flexShrink:0,fontVariationSettings:page===item.id?"'FILL' 1":"'FILL' 0"}}>{item.icon}</span>
              <span className="sb-label" style={{opacity:0,maxWidth:0,overflow:'hidden',transition:'opacity 200ms,max-width 260ms',whiteSpace:'nowrap',fontSize:13}}>{item.label}</span>
            </button>
          ))}
        </nav>
        {/* Logout */}
        <div style={{padding:'8px 8px',borderTop:'1px solid rgba(255,255,255,0.07)'}}>
          <button onClick={onLogout} className="sb-btn"
            style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'10px 0',borderRadius:10,border:'none',cursor:'pointer',background:'transparent',color:'rgba(255,255,255,0.45)',fontWeight:600,fontSize:13,transition:'all 150ms',justifyContent:'center'}}
            onMouseEnter={e=>{e.currentTarget.style.background='rgba(239,68,68,0.15)';e.currentTarget.style.color='#fca5a5';}}
            onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='rgba(255,255,255,0.45)';}}>
            <span className="material-symbols-outlined" style={{fontSize:20,flexShrink:0}}>logout</span>
            <span className="sb-label" style={{opacity:0,maxWidth:0,overflow:'hidden',transition:'opacity 200ms,max-width 260ms',whiteSpace:'nowrap',fontSize:13}}>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0,overflow:'hidden'}}>
        {/* Top bar */}
        <header style={{
          height:64, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'0 24px', background:'#ffffff', borderBottom:'1px solid #e8edf5',
          boxShadow:'0 2px 12px rgba(0,31,77,0.07)', zIndex:100, position:'relative',
        }}>
          {/* Left — hamburger + logo + school name */}
          <div style={{display:'flex',alignItems:'center',gap:12,minWidth:0}}>
            {isMobile && (
              <button onClick={()=>setMobileNavOpen(v=>!v)}
                style={{border:0,background:'transparent',cursor:'pointer',color:'#1960a3',padding:4,display:'flex',alignItems:'center',flexShrink:0}}>
                <span className="material-symbols-outlined" style={{fontSize:24}}>menu</span>
              </button>
            )}
            <div onClick={()=>setPage('pdash')} style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',minWidth:0}}>
              <img src={process.env.PUBLIC_URL+'/assets/logo.png'} alt="LKPS"
                style={{width:36,height:36,objectFit:'contain',flexShrink:0}}
                onError={e=>{e.target.style.display='none';}}/>
              {!isMobile && (
                <div>
                  <div style={{fontSize:13,fontWeight:900,color:'#002045',fontFamily:'Manrope,sans-serif',letterSpacing:'0.01em',lineHeight:1.1}}>LORD KRISHNA PUBLIC SCHOOL</div>
                  <div style={{fontSize:9,fontWeight:700,color:'#1960a3',letterSpacing:'0.12em',textTransform:'uppercase'}}>Parent Portal</div>
                </div>
              )}
            </div>
            {!isMobile && <>
              <div style={{width:1,height:28,background:'linear-gradient(180deg,transparent,#c7d9f5,transparent)',margin:'0 4px'}}/>
              <div style={{display:'flex',flexDirection:'column',lineHeight:1}}>
                <span style={{fontSize:9,fontWeight:800,color:'#1960a3',letterSpacing:'0.14em',textTransform:'uppercase'}}>Session</span>
                <span style={{fontSize:12,fontWeight:900,color:'#002045',fontFamily:'Manrope,sans-serif'}}>{db.settings?.year||'2026-27'}</span>
              </div>
            </>}
          </div>

          {/* Right — alert bell + profile popover */}
          <div style={{display:'flex',alignItems:'center',gap:isMobile?8:10}}>

            {/* Alert bell */}
            {(() => {
              const sid = child.sid || child.id;
              const pays = (db.pays||[]).filter(p=>p.sid===sid);
              const totalPaid = pays.reduce((s,p)=>s+(p.amt||0),0);
              const feeDue = Math.max(0,(child.fee||0)-totalPaid);
              const today = new Date().toISOString().split('T')[0];
              const upcomingExams = (db.exams||[]).filter(e=>(!e.cls||e.cls===child.cls)&&(()=>{const now=new Date();if(e.dt&&e.startTime&&e.endTime){const s=new Date(e.dt+'T'+e.startTime),en=new Date(e.dt+'T'+e.endTime);if(!isNaN(s)&&!isNaN(en))return now<=en;}if(e.dt){const d=new Date(e.dt);d.setHours(23,59,59);return now<=d;}return true;})()).slice(0,3);
              const upcomingEvents = (db.events||[]).filter(e=>e.dt>=today).slice(0,3);
              const alertCount = (feeDue>0?1:0) + upcomingExams.length + upcomingEvents.length;

              return (
                <div style={{position:'relative'}}>
                  <button onClick={()=>setAlertOpen(v=>!v)}
                    style={{position:'relative',width:36,height:36,borderRadius:10,border:'1.5px solid #e2e8f0',background:'#f8fafc',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:'#64748b',transition:'all 150ms'}}
                    onMouseEnter={e=>{e.currentTarget.style.background='#eef4ff';e.currentTarget.style.color='#1960a3';e.currentTarget.style.borderColor='#c7d9f5';}}
                    onMouseLeave={e=>{e.currentTarget.style.background='#f8fafc';e.currentTarget.style.color='#64748b';e.currentTarget.style.borderColor='#e2e8f0';}}>
                    <span className="material-symbols-outlined" style={{fontSize:20}}>notifications</span>
                    {alertCount > 0 && <span style={{position:'absolute',top:4,right:4,width:8,height:8,borderRadius:'50%',background:'#dc2626',border:'1.5px solid #fff'}}/>}
                  </button>
                  {alertOpen && (
                    <>
                      <div onClick={()=>setAlertOpen(false)} style={{position:'fixed',inset:0,zIndex:199}}/>
                      <div style={{position:'absolute',right:0,top:44,width:300,background:'#fff',borderRadius:14,boxShadow:'0 8px 32px rgba(0,31,77,0.15)',border:'1px solid #e2e8f0',zIndex:200,overflow:'hidden'}}>
                        <div style={{padding:'12px 16px',borderBottom:'1px solid #f1f5f9',fontSize:12,fontWeight:800,color:'#1e293b'}}>Alerts & Reminders</div>
                        <div style={{maxHeight:320,overflowY:'auto'}}>
                          {feeDue > 0 && (
                            <div onClick={()=>{setPage('pfee');setAlertOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderBottom:'1px solid #f1f5f9',cursor:'pointer',background:'#fff'}}
                              onMouseEnter={e=>e.currentTarget.style.background='#fef2f2'}
                              onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                              <div style={{width:32,height:32,borderRadius:8,background:'#fee2e2',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span className="material-symbols-outlined" style={{fontSize:16,color:'#dc2626',fontVariationSettings:"'FILL' 1"}}>payments</span>
                              </div>
                              <div>
                                <div style={{fontSize:12,fontWeight:700,color:'#1e293b'}}>Fee Due</div>
                                <div style={{fontSize:11,color:'#dc2626',fontWeight:600}}>₹{feeDue.toLocaleString('en-IN')} pending</div>
                              </div>
                            </div>
                          )}
                          {upcomingExams.map((e,i)=>(
                            <div key={i} onClick={()=>{setPage('pexam');setAlertOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderBottom:'1px solid #f1f5f9',cursor:'pointer',background:'#fff'}}
                              onMouseEnter={ev=>ev.currentTarget.style.background='#f5f3ff'}
                              onMouseLeave={ev=>ev.currentTarget.style.background='#fff'}>
                              <div style={{width:32,height:32,borderRadius:8,background:'#ede9fe',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span className="material-symbols-outlined" style={{fontSize:16,color:'#7c3aed',fontVariationSettings:"'FILL' 1"}}>quiz</span>
                              </div>
                              <div>
                                <div style={{fontSize:12,fontWeight:700,color:'#1e293b'}}>{e.name}</div>
                                <div style={{fontSize:11,color:'#7c3aed',fontWeight:600}}>{e.dt||'TBD'} · {e.su||''}</div>
                              </div>
                            </div>
                          ))}
                          {upcomingEvents.map((e,i)=>(
                            <div key={i} onClick={()=>{setPage('pcal');setAlertOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderBottom:'1px solid #f1f5f9',cursor:'pointer',background:'#fff'}}
                              onMouseEnter={ev=>ev.currentTarget.style.background='#f0fdf4'}
                              onMouseLeave={ev=>ev.currentTarget.style.background='#fff'}>
                              <div style={{width:32,height:32,borderRadius:8,background:'#dcfce7',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <span className="material-symbols-outlined" style={{fontSize:16,color:'#059669',fontVariationSettings:"'FILL' 1"}}>event</span>
                              </div>
                              <div>
                                <div style={{fontSize:12,fontWeight:700,color:'#1e293b'}}>{e.title}</div>
                                <div style={{fontSize:11,color:'#059669',fontWeight:600}}>{e.dt}</div>
                              </div>
                            </div>
                          ))}
                          {alertCount === 0 && (
                            <div style={{padding:'24px 16px',textAlign:'center',color:'#94a3b8',fontSize:12}}>No alerts right now</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            <div style={{width:1,height:28,background:'#e2e8f0'}}/>

            {/* Profile popover — click avatar to open, sign out inside */}
            <div ref={profileRef} style={{position:'relative'}}>
              <button onClick={()=>setProfileOpen(o=>!o)}
                style={{display:'flex',alignItems:'center',gap:isMobile?0:8,background:'transparent',border:'none',cursor:'pointer',padding:isMobile?0:'4px 6px',borderRadius:10,transition:'background 150ms'}}
                onMouseEnter={e=>e.currentTarget.style.background='#f1f5f9'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{width:34,height:34,borderRadius:'50%',background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:'#fff',overflow:'hidden',flexShrink:0,boxShadow:'0 2px 8px rgba(25,96,163,0.25)'}}>
                  {photo ? <img src={photo} alt={name} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : ini}
                </div>
                {!isMobile && <>
                  <div style={{textAlign:'left'}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#1e293b',lineHeight:1.3}}>{name}</div>
                    <div style={{fontSize:10,color:'#94a3b8'}}>Class {child.cls||'—'}</div>
                  </div>
                  <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8',transition:'transform 200ms',transform:profileOpen?'rotate(180deg)':'rotate(0deg)'}}>expand_more</span>
                </>}
              </button>

              {profileOpen && (
                <div style={{position:'absolute',top:'calc(100% + 10px)',right:0,width:260,background:'#fff',borderRadius:16,boxShadow:'0 8px 32px rgba(0,32,69,0.14)',border:'1px solid #e8edf5',zIndex:999,overflow:'hidden',animation:'pp-pop-in 150ms ease'}}>
                  <style>{`@keyframes pp-pop-in{from{opacity:0;transform:translateY(-6px) scale(0.97)}to{opacity:1;transform:none}}`}</style>
                  {/* Profile header */}
                  <div style={{padding:'20px 20px 16px',background:'linear-gradient(135deg,#eef4ff,#f0fdf4)',borderBottom:'1px solid #e8edf5'}}>
                    <div style={{display:'flex',alignItems:'center',gap:14}}>
                      <div style={{width:48,height:48,borderRadius:'50%',background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:18,color:'#fff',boxShadow:'0 4px 12px rgba(25,96,163,0.3)',flexShrink:0,overflow:'hidden'}}>
                        {photo ? <img src={photo} alt={name} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : ini}
                      </div>
                      <div>
                        <div style={{fontSize:15,fontWeight:800,color:'#1e293b',fontFamily:'Manrope,sans-serif'}}>{name}</div>
                        <div style={{fontSize:11,color:'#64748b',marginTop:2}}>Class {child.cls||'—'}</div>
                        <span style={{display:'inline-block',marginTop:5,fontSize:10,fontWeight:700,color:'#059669',background:'#dcfce7',padding:'2px 10px',borderRadius:20,letterSpacing:'0.04em'}}>Student</span>
                      </div>
                    </div>
                  </div>
                  {/* Info rows */}
                  <div style={{padding:'12px 20px'}}>
                    {child.roll && (
                      <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #f1f5f9'}}>
                        <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8'}}>badge</span>
                        <div>
                          <div style={{fontSize:10,color:'#94a3b8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em'}}>Roll No.</div>
                          <div style={{fontSize:13,color:'#1e293b',fontWeight:600}}>{child.roll}</div>
                        </div>
                      </div>
                    )}
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #f1f5f9'}}>
                      <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8'}}>school</span>
                      <div>
                        <div style={{fontSize:10,color:'#94a3b8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em'}}>Session</div>
                        <div style={{fontSize:13,color:'#1e293b',fontWeight:600}}>{db.settings?.year||'2026-27'}</div>
                      </div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0'}}>
                      <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8'}}>person</span>
                      <div>
                        <div style={{fontSize:10,color:'#94a3b8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em'}}>View Profile</div>
                        <button onClick={()=>{setPage('pprofile');setProfileOpen(false);}} style={{fontSize:13,color:'#1960a3',fontWeight:600,background:'none',border:'none',cursor:'pointer',padding:0}}>Open full profile →</button>
                      </div>
                    </div>
                  </div>
                  {/* Sign out */}
                  <div style={{padding:'0 12px 12px'}}>
                    <button onClick={()=>{setProfileOpen(false);onLogout();}}
                      style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',padding:'10px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#ef4444,#dc2626)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',boxShadow:'0 4px 12px rgba(239,68,68,0.25)',transition:'opacity 150ms'}}
                      onMouseEnter={e=>e.currentTarget.style.opacity='0.9'}
                      onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                      <span className="material-symbols-outlined" style={{fontSize:16}}>logout</span>
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        <main style={{flex:1,overflowY:'auto',padding:'28px 32px'}} className="pp-main-pad">
          {pages[page] || pages.pdash}
        </main>
      </div>
    </div>
  );
}

// ── Parent Report Card ────────────────────────────────────────────
function ParentReportCard({ db, child }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getStudentProfile()
      .then(r => setProfile(r.student))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  const data = profile || child;
  // Report card is available if _rcMarks exists (set by admin when saving/generating)
  // Fall back to class subjects from db if _rcSubjects not stored
  const fallbackSubs = (() => {
    const cls = data.cls || child.cls || '';
    const clsObj = (db.classes||[]).find(c => c.name === cls);
    if (clsObj?.subs) return clsObj.subs;
    // default subjects for common classes
    const lower = cls.toLowerCase().replace(/\./g,'');
    if (['playgroup','nursery','lkg','ukg'].includes(lower)) return 'Hindi, English, Mathematics, Rhymes';
    return 'English, Hindi, Mathematics, Science, S.St., Computer';
  })();
  const dataWithSubs = data._rcSubjects ? data : { ...data, _rcSubjects: fallbackSubs };
  const hasRC = !!(data._rcMarks);
  const genDate = data._rcGeneratedAt ? new Date(data._rcGeneratedAt).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : '';
  const logo = process.env.PUBLIC_URL + '/assets/logo.png';

  // Build HTML on-demand from stored _rc* fields
  const html = hasRC ? buildRCHtmlForStudent(dataWithSubs, db.settings || {}, logo) : null;

  const doPrint = () => {
    if (!html) return;
    const w = window.open('','_blank','width=900,height=700');
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 800);
  };

  if (loading) return (
    <div className="pp-fadein" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:300}}>
      <div style={{textAlign:'center',color:'#94a3b8'}}>
        <span className="material-symbols-outlined" style={{fontSize:32,display:'block',marginBottom:8,animation:'pp-spin 1s linear infinite'}}>progress_activity</span>
        <div style={{fontSize:13,fontWeight:600}}>Loading report card…</div>
      </div>
    </div>
  );

  if (!hasRC || !html) return (
    <div className="pp-fadein">
      <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',marginBottom:24}}>Report Card</h1>
      <div style={{background:'#fff',borderRadius:20,padding:'48px 32px',boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9',textAlign:'center',maxWidth:480,margin:'0 auto'}}>
        <div style={{width:72,height:72,borderRadius:20,background:'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
          <span className="material-symbols-outlined" style={{fontSize:36,color:'#94a3b8'}}>description</span>
        </div>
        <div style={{fontSize:18,fontWeight:800,color:'#1e293b',marginBottom:10,fontFamily:'Manrope,sans-serif'}}>Report Card Not Available Yet</div>
        <div style={{fontSize:13,color:'#64748b',lineHeight:1.7,marginBottom:24}}>
          Your report card hasn't been published by the school yet.<br/>
          Please contact the school office for more information.
        </div>
        <div style={{display:'inline-flex',alignItems:'center',gap:10,background:'#eef4ff',border:'1px solid #c7d9f5',borderRadius:12,padding:'12px 20px'}}>
          <span className="material-symbols-outlined" style={{fontSize:18,color:'#1960a3',fontVariationSettings:"'FILL' 1"}}>phone</span>
          <div style={{textAlign:'left'}}>
            <div style={{fontSize:11,color:'#64748b',fontWeight:600}}>School Office</div>
            <div style={{fontSize:13,color:'#1960a3',fontWeight:700}}>{db.settings?.phone || '+91 9997360040'}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const A4_W = 794;
  const A4_H = 1123;
  const containerW = Math.min(typeof window !== 'undefined' ? window.innerWidth - 80 : 700, 700);
  const scale = containerW / A4_W;

  return (
    <div className="pp-fadein">
      <div style={{marginBottom:24,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',margin:0}}>Report Card</h1>
          {genDate && <div style={{fontSize:12,color:'#94a3b8',marginTop:4}}>Published on {genDate}</div>}
        </div>
        <button onClick={doPrint}
          style={{display:'flex',alignItems:'center',gap:6,padding:'10px 20px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',boxShadow:'0 4px 12px rgba(0,32,69,0.2)'}}>
          <span className="material-symbols-outlined" style={{fontSize:18}}>print</span>
          Print / Download
        </button>
      </div>

      <div style={{display:'flex',justifyContent:'center'}}>
        <div style={{background:'#fff',borderRadius:20,padding:20,boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9',display:'inline-block',width:containerW+40}}>
          <div style={{width:containerW,height:Math.round(A4_H*scale),overflow:'hidden',borderRadius:8,background:'#E8F4FD'}}>
            <iframe
              srcDoc={html}
              scrolling="no"
              style={{width:A4_W,height:A4_H,border:'none',transform:`scale(${scale})`,transformOrigin:'top left',display:'block'}}
            />
          </div>
          <div style={{textAlign:'center',marginTop:12,fontSize:11,color:'#94a3b8',fontWeight:600}}>
            Tap Print / Download to save a copy
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Parent ID Card ────────────────────────────────────────────────
function ParentIDCard({ db, child }) {
  const [showBack, setShowBack] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState(false);
  const sid = child.sid || child.id;
  const photo = (db.photos||{})[sid] || child.photo || null;

  const fetchProfile = () => {
    setLoading(true); setFetchErr(false);
    getStudentProfile()
      .then(r => { setProfile(r.student); setFetchErr(false); })
      .catch(() => { setProfile(null); setFetchErr(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProfile(); }, []);

  const data = profile || child;
  const generated = data._idCardGenerated;
  const theme = data._idCardTheme || 'blue';
  const year  = data._idCardYear  || db.settings?.year || '2026-27';
  const prin  = data._idCardPrin  || db.settings?.prin || '';
  const phone = data._idCardPhone || db.settings?.phone || '';
  const logo  = process.env.PUBLIC_URL + '/assets/logo.png'; // same logo as header

  const { CW, CH } = CARD_DIMS;
  const SCALE = 0.46;
  const W = Math.round(CW * SCALE);
  const H = Math.round(CH * SCALE);

  const frontHTML = generated ? buildCard(data, photo, logo, phone, year, prin, theme) : '';
  const backHTML  = generated ? buildCardBack(data, logo, phone) : '';

  const doPrint = () => {
    const css = `body{margin:0;padding:20px;background:#e2e8f0;display:flex;gap:24px;justify-content:center;flex-wrap:wrap;}.label{font-size:10px;color:#666;text-align:center;margin-bottom:6px;font-family:Arial;font-weight:600;text-transform:uppercase;}@media print{body{background:#fff;padding:0;gap:0;}.side{page-break-after:always;display:flex;flex-direction:column;align-items:center;padding:10px;}.label{display:none;}@page{size:${CW+40}px ${CH+40}px;margin:0;}}`;
    const w = window.open('','_blank','width=820,height=620');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>${css}</style></head><body><div class="side"><div class="label">Front</div>${frontHTML}</div><div class="side"><div class="label">Back</div>${backHTML}</div><script>window.onload=()=>window.print()<\/script></body></html>`);
    w.document.close();
  };

  if (loading) return (
    <div className="pp-fadein" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:300}}>
      <div style={{textAlign:'center',color:'#94a3b8'}}>
        <span className="material-symbols-outlined" style={{fontSize:32,display:'block',marginBottom:8,animation:'pp-spin 1s linear infinite'}}>progress_activity</span>
        <div style={{fontSize:13,fontWeight:600}}>Loading ID card…</div>
      </div>
    </div>
  );

  if (!generated) return (
    <div className="pp-fadein">
      <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',marginBottom:24}}>ID Card</h1>
      <div style={{background:'#fff',borderRadius:20,padding:'48px 32px',boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9',textAlign:'center',maxWidth:480,margin:'0 auto'}}>
        <div style={{width:72,height:72,borderRadius:20,background:'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
          <span className="material-symbols-outlined" style={{fontSize:36,color:'#94a3b8'}}>badge</span>
        </div>
        <div style={{fontSize:18,fontWeight:800,color:'#1e293b',marginBottom:10,fontFamily:'Manrope,sans-serif'}}>ID Card Not Generated Yet</div>
        <div style={{fontSize:13,color:'#64748b',lineHeight:1.7,marginBottom:24}}>
          Your student ID card hasn't been generated by the school yet.<br/>
          Please contact the school office to request your ID card.
        </div>
        <button onClick={fetchProfile} disabled={loading}
          style={{display:'inline-flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:10,border:'1.5px solid #c7d9f5',background:'#eef4ff',color:'#1960a3',fontWeight:700,fontSize:12,cursor:'pointer',marginBottom:20}}>
          <span className="material-symbols-outlined" style={{fontSize:16}}>refresh</span>
          {loading ? 'Checking…' : 'Check Again'}
        </button>
        {fetchErr && <div style={{fontSize:12,color:'#dc2626',marginBottom:16}}>Could not reach server. Please check your connection.</div>}
        <div style={{display:'inline-flex',alignItems:'center',gap:10,background:'#eef4ff',border:'1px solid #c7d9f5',borderRadius:12,padding:'12px 20px'}}>
          <span className="material-symbols-outlined" style={{fontSize:18,color:'#1960a3',fontVariationSettings:"'FILL' 1"}}>phone</span>
          <div style={{textAlign:'left'}}>
            <div style={{fontSize:11,color:'#64748b',fontWeight:600}}>School Office</div>
            <div style={{fontSize:13,color:'#1960a3',fontWeight:700}}>{db.settings?.phone || '+91 9997360040'}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const genDate = data._idCardGeneratedAt ? new Date(data._idCardGeneratedAt).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}) : '';

  return (
    <div className="pp-fadein">
      <div style={{marginBottom:24,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',margin:0}}>ID Card</h1>
          {genDate && <div style={{fontSize:12,color:'#94a3b8',marginTop:4}}>Generated on {genDate}</div>}
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <button onClick={fetchProfile} disabled={loading}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'#f8fafc',color:'#64748b',fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>
            <span className="material-symbols-outlined" style={{fontSize:15}}>refresh</span>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <div style={{display:'flex',background:'#f8fafc',borderRadius:10,border:'1px solid #e2e8f0',overflow:'hidden'}}>
            {[['Front',false],['Back',true]].map(([lbl,val])=>(
              <button key={lbl} onClick={()=>setShowBack(val)}
                style={{padding:'8px 18px',border:'none',fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',transition:'all 150ms',
                  background:showBack===val?'#1960a3':'transparent',
                  color:showBack===val?'#fff':'#64748b'}}>
                {lbl}
              </button>
            ))}
          </div>
          <button onClick={doPrint}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>
            <span className="material-symbols-outlined" style={{fontSize:16}}>print</span>
            Print Card
          </button>
        </div>
      </div>

      <div style={{display:'flex',justifyContent:'center'}}>
        <div style={{background:'#fff',borderRadius:20,padding:24,boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9',display:'inline-block'}}>
          <div style={{width:W+16,height:H+16,padding:8,overflow:'hidden',borderRadius:12,background:'#e8edf5'}}>
            <iframe
              key={showBack?'back':'front'}
              srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:transparent;}</style></head><body>${showBack?backHTML:frontHTML}</body></html>`}
              scrolling="no"
              style={{width:CW,height:CH,border:'none',transform:`scale(${SCALE})`,transformOrigin:'top left',display:'block'}}
            />
          </div>
          <div style={{textAlign:'center',marginTop:12,fontSize:11,color:'#94a3b8',fontWeight:600}}>
            {showBack ? 'Back Side' : 'Front Side'} · Tap Print to get a physical copy
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Parent Teachers (My Teachers / Class Hierarchy) ──────────────
function ParentTeachers({ db, child }) {
  const cls = child.cls || '';
  const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  // Teachers who teach this class (from timetable or teacher.cls)
  const ttEntries = (db.tt||[]).filter(p => p.cls === cls && p.su && p.tea);

  // Build subject → teacher map from timetable
  const subTeacherMap = {};
  ttEntries.forEach(p => {
    if (!subTeacherMap[p.su]) subTeacherMap[p.su] = new Set();
    subTeacherMap[p.su].add(p.tea);
  });

  // Also include teachers whose cls matches
  const classTeachers = (db.teachers||[]).filter(t => t.cls === cls || (t.su && ttEntries.some(p => p.tea === (t.fn+' '+t.ln).trim())));

  // Build teacher cards: merge timetable + teacher directory
  const teacherMap = {};
  // From timetable
  ttEntries.forEach(p => {
    const key = p.tea;
    if (!teacherMap[key]) {
      const t = (db.teachers||[]).find(x => (x.fn+' '+x.ln).trim() === key || x.fn === key);
      teacherMap[key] = { name: key, subjects: new Set(), photo: t ? (db.tphotos||{})[t.tid||t.id] : null, qual: t?.qual||'', ph: t?.ph||'' };
    }
    if (p.su) teacherMap[key].subjects.add(p.su);
  });
  // From teacher directory (class teacher)
  classTeachers.forEach(t => {
    const key = (t.fn+' '+t.ln).trim();
    if (!teacherMap[key]) {
      teacherMap[key] = { name: key, subjects: new Set(), photo: (db.tphotos||{})[t.tid||t.id]||null, qual: t.qual||'', ph: t.ph||'' };
    }
    if (t.su) t.su.split(',').map(s=>s.trim()).filter(Boolean).forEach(s => teacherMap[key].subjects.add(s));
  });

  const teachers = Object.values(teacherMap).map(t => ({ ...t, subjects: [...t.subjects] }));

  // Today's timetable for this class
  const today = DAYS[new Date().getDay()-1] || 'Monday';
  const todaySlots = (db.slots||[]).map(sl => {
    const p = (db.tt||[]).find(x => x.cls===cls && x.day===today && x.sid===sl.id);
    return { slot: sl, period: p };
  }).filter(x => x.period);

  return (
    <div className="pp-fadein">
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>Class {cls}</div>
        <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',margin:0}}>My Teachers</h1>
      </div>

      {/* Teacher cards */}
      {teachers.length === 0 ? (
        <div style={{background:'#fff',borderRadius:16,padding:'48px 32px',textAlign:'center',boxShadow:'0 1px 8px rgba(0,31,77,0.06)',marginBottom:20}}>
          <span className="material-symbols-outlined" style={{fontSize:40,color:'#e2e8f0',display:'block',marginBottom:12}}>school</span>
          <div style={{fontSize:14,color:'#94a3b8',fontWeight:600}}>No teacher information available yet</div>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:16,marginBottom:28}}>
          {teachers.map((t,i) => {
            const ini = t.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
            return (
              <div key={i} style={{background:'#fff',borderRadius:16,padding:'20px',boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9'}}>
                <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:14}}>
                  <div style={{width:52,height:52,borderRadius:'50%',background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:800,color:'#fff',overflow:'hidden',flexShrink:0}}>
                    {t.photo ? <img src={t.photo} alt={t.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : ini}
                  </div>
                  <div>
                    <div style={{fontSize:15,fontWeight:800,color:'#1e293b',fontFamily:'Manrope,sans-serif'}}>{t.name}</div>
                    {t.qual && <div style={{fontSize:11,color:'#64748b',marginTop:2}}>{t.qual}</div>}
                    {t.ph && <div style={{fontSize:11,color:'#94a3b8',marginTop:1}}>{t.ph}</div>}
                  </div>
                </div>
                {t.subjects.length > 0 && (
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:8}}>Teaches</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                      {t.subjects.map(s => (
                        <span key={s} style={{background:'#eef4ff',color:'#1960a3',fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,border:'1px solid #c7d9f5'}}>{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Today's schedule */}
      <div style={{background:'#fff',borderRadius:16,padding:'20px 24px',boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9',marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:800,color:'#1e293b',marginBottom:4,display:'flex',alignItems:'center',gap:6}}>
          <span className="material-symbols-outlined" style={{fontSize:16,color:'#1960a3',fontVariationSettings:"'FILL' 1"}}>today</span>
          Today's Schedule
          <span style={{fontSize:11,color:'#94a3b8',fontWeight:600,marginLeft:4}}>({today})</span>
        </div>
        {todaySlots.length === 0 ? (
          <div style={{fontSize:13,color:'#94a3b8',padding:'16px 0'}}>No periods scheduled for today</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:12}}>
            {todaySlots.map(({slot,period},i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',borderRadius:10,background:'#f8fafc',border:'1px solid #f1f5f9'}}>
                <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',minWidth:80,flexShrink:0}}>{slot.s}–{slot.e}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#1e293b'}}>{period.su}</div>
                  {period.tea && <div style={{fontSize:11,color:'#64748b',marginTop:1}}>{period.tea}</div>}
                </div>
                {period.rm && <div style={{fontSize:10,fontWeight:600,color:'#94a3b8'}}>Room {period.rm}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full weekly timetable */}
      <div style={{background:'#fff',borderRadius:16,padding:'20px 24px',boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9'}}>
        <div style={{fontSize:13,fontWeight:800,color:'#1e293b',marginBottom:16,display:'flex',alignItems:'center',gap:6}}>
          <span className="material-symbols-outlined" style={{fontSize:16,color:'#1960a3',fontVariationSettings:"'FILL' 1"}}>calendar_view_week</span>
          Weekly Timetable
        </div>
        {(db.slots||[]).length === 0 ? (
          <div style={{fontSize:13,color:'#94a3b8'}}>No timetable configured yet</div>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:500}}>
              <thead>
                <tr>
                  <th style={{padding:'8px 12px',fontSize:10,fontWeight:800,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',background:'#f8fafc',borderBottom:'1px solid #f1f5f9',textAlign:'left',minWidth:80}}>Time</th>
                  {DAYS.map(d => (
                    <th key={d} style={{padding:'8px 10px',fontSize:10,fontWeight:800,color: d===today?'#1960a3':'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',background: d===today?'#eef4ff':'#f8fafc',borderBottom:'1px solid #f1f5f9',textAlign:'center'}}>
                      {d.slice(0,3)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(db.slots||[]).map((sl,si) => (
                  <tr key={sl.id} style={{borderBottom:'1px solid #f8fafc'}}>
                    <td style={{padding:'8px 12px',fontSize:11,fontWeight:600,color:'#94a3b8',whiteSpace:'nowrap'}}>{sl.s}–{sl.e}</td>
                    {DAYS.map(d => {
                      const p = (db.tt||[]).find(x => x.cls===cls && x.day===d && x.sid===sl.id);
                      const isToday = d === today;
                      return (
                        <td key={d} style={{padding:'6px 8px',textAlign:'center',background: isToday?'#f5f9ff':'transparent'}}>
                          {p ? (
                            <div>
                              <div style={{fontSize:11,fontWeight:700,color:'#1e293b'}}>{p.su}</div>
                              {p.tea && <div style={{fontSize:9,color:'#94a3b8',marginTop:1}}>{p.tea.split(' ').pop()}</div>}
                            </div>
                          ) : <span style={{color:'#e2e8f0',fontSize:12}}>—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Parent Profile ────────────────────────────────────────────────
function ParentProfile({ child, db, activeSessionId }) {
  // Hooks first
  const [profileData, setProfileData] = useState(null);
  const [emailStep, setEmailStep] = useState('idle');
  const [currentOtp, setCurrentOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newOtp, setNewOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);

  // Fetch full student profile from server on mount
  useEffect(() => {
    getStudentProfile()
      .then(r => { if (r?.student) setProfileData(r.student); })
      .catch(() => {});
  }, []);

  // Use server profile if available, fall back to child from db
  const data = profileData || child;

  const [displayEmail, setDisplayEmail] = useState(child.em || child.email || '');
  const photo = (db.photos||{})[data.sid||data.id] || data.photo || child.photo || '';
  const name = (data.fn||child.fn||'') + ' ' + (data.ln||child.ln||'');
  const ini = name.trim().split(/\s+/).map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const dob = (() => {
    const d = data.dob || child.dob;
    if (!d) return '—';
    if (/^\d{2}-\d{2}-\d{4}$/.test(d)) return d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) { const [y,m,dd] = d.split('-'); return `${dd}-${m}-${y}`; }
    const dt = new Date(d);
    if (!isNaN(dt)) return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`;
    return d;
  })();

  const resetFlow = () => { setEmailStep('idle'); setCurrentOtp(''); setNewEmail(''); setNewOtp(''); setEmailErr(''); setEmailBusy(false); };

  const startEdit = async () => {
    if (!displayEmail) { setEmailStep('enter-new'); return; }
    setEmailBusy(true); setEmailErr('');
    try {
      const r = await studentRequestChangeEmailOtp();
      setMaskedEmail(r.maskedEmail || '');
      setEmailStep('verify-current');
    } catch(e) { setEmailErr(e.message || 'Failed to send OTP'); }
    finally { setEmailBusy(false); }
  };

  const verifyCurrentOtp = async () => {
    if (!currentOtp) { setEmailErr('Enter the OTP'); return; }
    if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) { setEmailErr('Enter a valid new email'); return; }
    setEmailBusy(true); setEmailErr('');
    try {
      await studentVerifyCurrentEmailOtp(currentOtp, newEmail);
      setEmailStep('verify-new');
    } catch(e) {
      setEmailErr(e.message || 'Invalid OTP');
      // reset new email input so user can't proceed with wrong data
      setNewEmail('');
    }
    finally { setEmailBusy(false); }
  };

  const confirmNewEmail = async () => {
    if (!newOtp) { setEmailErr('Enter the OTP'); return; }
    setEmailBusy(true); setEmailErr('');
    try {
      await studentConfirmNewEmail(newOtp);
      setDisplayEmail(newEmail);
      child.em = newEmail; child.email = newEmail;
      setEmailStep('done');
      setTimeout(() => setEmailStep('idle'), 2500);
    } catch(e) {
      setEmailErr(e.message || 'Invalid OTP — please re-enter your email');
      setNewEmail('');
      setNewOtp('');
      setEmailStep('enter-new');
    }
    finally { setEmailBusy(false); }
  };

  // Password change flow
  const [pwStep, setPwStep] = useState('idle'); // idle | form | done
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const resetPw = () => { setPwStep('idle'); setCurPw(''); setNewPw(''); setConfPw(''); setPwErr(''); setPwBusy(false); };

  const doChangePw = async () => {
    if (!curPw) { setPwErr('Enter your current password'); return; }
    if (!newPw || newPw.length < 6) { setPwErr('New password must be at least 6 characters'); return; }
    if (newPw !== confPw) { setPwErr('Passwords do not match'); return; }
    if (newPw === curPw) { setPwErr('New password must be different from current'); return; }
    setPwBusy(true); setPwErr('');
    try {
      await parentChangePassword(curPw, newPw);
      setPwStep('done');
      setTimeout(() => resetPw(), 2500);
    } catch(e) {
      setPwErr(e.message || 'Failed to change password');
    } finally { setPwBusy(false); }
  };

  const row = (label, val) => val ? (
    <div style={{display:'flex',gap:16,padding:'12px 0',borderBottom:'1px solid #f1f5f9'}}>
      <div style={{fontSize:12,color:'#64748b',fontWeight:600,minWidth:140}}>{label}</div>
      <div style={{fontSize:13,color:'#1e293b',fontWeight:600}}>{val}</div>
    </div>
  ) : null;

  const inp = { width:'100%', padding:'10px 12px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#f8fafc', fontSize:13, color:'#0f172a', outline:'none', boxSizing:'border-box' };

  return (
    <div>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>Student Profile</div>
        <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',margin:0}}>{name}</h1>
      </div>

      {/* Profile card */}
      <div style={{background:'#fff',borderRadius:16,padding:'28px',boxShadow:'0 1px 8px rgba(0,31,77,0.06)',marginBottom:20,display:'flex',gap:24,alignItems:'flex-start',flexWrap:'wrap'}}>
        <div style={{width:100,height:100,borderRadius:16,background:'linear-gradient(135deg,#1960a3,#60a5fa)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32,fontWeight:900,color:'#fff',overflow:'hidden',flexShrink:0}}>
          {photo ? <img src={photo} alt={name} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : ini}
        </div>
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:22,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',marginBottom:4}}>{name}</div>
          <div style={{fontSize:13,color:'#64748b',marginBottom:12}}>Class {data.cls||child.cls||'—'} · {db.settings?.year||''}</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {(data.roll||child.roll) && <span style={{background:'#eef4ff',color:'#1960a3',fontSize:11,fontWeight:700,padding:'4px 12px',borderRadius:20,border:'1px solid #c7d9f5'}}>Roll: {data.roll||child.roll}</span>}
            {(data.admno||child.admno) && <span style={{background:'#f0fdf4',color:'#059669',fontSize:11,fontWeight:700,padding:'4px 12px',borderRadius:20,border:'1px solid #bbf7d0'}}>Adm: {data.admno||child.admno}</span>}
            {(data.blood||child.blood) && <span style={{background:'#fef2f2',color:'#dc2626',fontSize:11,fontWeight:700,padding:'4px 12px',borderRadius:20,border:'1px solid #fecaca'}}>{data.blood||child.blood}</span>}
          </div>
        </div>
      </div>

      {/* Details */}
      <div style={{background:'#fff',borderRadius:16,padding:'20px 24px',boxShadow:'0 1px 8px rgba(0,31,77,0.06)'}}>
        <div style={{fontSize:12,fontWeight:800,color:'#1e293b',marginBottom:4}}>Personal Information</div>
        {row('Date of Birth', dob)}
        {row('Gender', data.gn||child.gn)}
        {row('Class / Section', data.cls||child.cls)}
        {row('Roll Number', data.roll||child.roll)}
        {row('Admission No.', data.admno||child.admno)}
        {row('Blood Group', data.blood||child.blood)}
        {row('Caste', data.caste||child.caste)}
        {row('Aadhaar No.', data.aadhar||child.aadhar)}
        <div style={{fontSize:12,fontWeight:800,color:'#1e293b',margin:'16px 0 4px'}}>Family Information</div>
        {row("Father's Name", data.father||child.father)}
        {row("Mother's Name", data.mother||child.mother)}
        {row("Father's Phone", data.fphone||child.fphone)}
        {row('Student Phone', data.ph||child.ph)}

        {/* Email row with edit */}
        <div style={{display:'flex',gap:16,padding:'12px 0',borderBottom:'1px solid #f1f5f9',alignItems:'flex-start',flexWrap:'wrap'}}>
          <div style={{fontSize:12,color:'#64748b',fontWeight:600,minWidth:140,paddingTop:2}}>Email</div>
          <div style={{flex:1,minWidth:200}}>
            {emailStep === 'idle' && (
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                <span style={{fontSize:13,color:'#1e293b',fontWeight:600}}>{displayEmail || <span style={{color:'#94a3b8'}}>Not set</span>}</span>
                <button onClick={startEdit} disabled={emailBusy}
                  style={{fontSize:11,fontWeight:700,color:'#1960a3',background:'#eef4ff',border:'1px solid #c7d9f5',borderRadius:20,padding:'3px 12px',cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                  <span className="material-symbols-outlined" style={{fontSize:13}}>edit</span>
                  {emailBusy ? 'Sending…' : displayEmail ? 'Change' : 'Add'}
                </button>
              </div>
            )}

            {emailStep === 'verify-current' && (
              <div style={{maxWidth:340}}>
                <div style={{fontSize:12,color:'#0369a1',fontWeight:600,marginBottom:8}}>OTP sent to <b>{maskedEmail}</b></div>
                {emailErr && <div style={{background:'#fef2f2',color:'#dc2626',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:600,marginBottom:8,border:'1px solid #fecaca'}}>{emailErr}</div>}
                <input value={currentOtp} onChange={e=>{setCurrentOtp(e.target.value);setEmailErr('');}} placeholder="Enter OTP from current email" maxLength={6} style={{...inp,marginBottom:8}} onKeyDown={e=>e.key==='Enter'&&verifyCurrentOtp()}/>
                <div style={{fontSize:12,color:'#64748b',marginBottom:8}}>New email address</div>
                <input value={newEmail} onChange={e=>{setNewEmail(e.target.value);setEmailErr('');}} type="email" placeholder="new@email.com" style={{...inp,marginBottom:10}} onKeyDown={e=>e.key==='Enter'&&verifyCurrentOtp()}/>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={verifyCurrentOtp} disabled={emailBusy}
                    style={{flex:1,padding:'9px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                    {emailBusy ? 'Verifying…' : 'Verify & Continue'}
                  </button>
                  <button onClick={resetFlow} style={{padding:'9px 14px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:600,fontSize:13,cursor:'pointer'}}>Cancel</button>
                </div>
              </div>
            )}

            {emailStep === 'enter-new' && (
              <div style={{maxWidth:340}}>
                <div style={{fontSize:12,color:'#64748b',marginBottom:8}}>Enter your email address</div>
                {emailErr && <div style={{background:'#fef2f2',color:'#dc2626',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:600,marginBottom:8,border:'1px solid #fecaca'}}>{emailErr}</div>}
                <input value={newEmail} onChange={e=>{setNewEmail(e.target.value);setEmailErr('');}} type="email" placeholder="your@email.com" style={{...inp,marginBottom:10}}/>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={async()=>{
                    if(!newEmail||!/\S+@\S+\.\S+/.test(newEmail)){setEmailErr('Enter a valid email');return;}
                    setEmailBusy(true);setEmailErr('');
                    try{ await studentVerifyCurrentEmailOtp('NO_CURRENT', newEmail); setEmailStep('verify-new'); }
                    catch(e){ setEmailErr(e.message||'Failed'); } finally{setEmailBusy(false);}
                  }} disabled={emailBusy}
                    style={{flex:1,padding:'9px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                    {emailBusy ? 'Sending OTP…' : 'Send OTP'}
                  </button>
                  <button onClick={resetFlow} style={{padding:'9px 14px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:600,fontSize:13,cursor:'pointer'}}>Cancel</button>
                </div>
              </div>
            )}

            {emailStep === 'verify-new' && (
              <div style={{maxWidth:340}}>
                <div style={{fontSize:12,color:'#0369a1',fontWeight:600,marginBottom:8}}>OTP sent to <b>{newEmail}</b></div>
                {emailErr && <div style={{background:'#fef2f2',color:'#dc2626',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:600,marginBottom:8,border:'1px solid #fecaca'}}>{emailErr}</div>}
                <input value={newOtp} onChange={e=>{setNewOtp(e.target.value);setEmailErr('');}} placeholder="Enter OTP from new email" maxLength={6} style={{...inp,marginBottom:10}} onKeyDown={e=>e.key==='Enter'&&confirmNewEmail()}/>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={confirmNewEmail} disabled={emailBusy}
                    style={{flex:1,padding:'9px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#059669,#047857)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                    {emailBusy ? 'Saving…' : 'Confirm & Save'}
                  </button>
                  <button onClick={resetFlow} style={{padding:'9px 14px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:600,fontSize:13,cursor:'pointer'}}>Cancel</button>
                </div>
              </div>
            )}

            {emailStep === 'done' && (
              <div style={{display:'flex',alignItems:'center',gap:8,color:'#059669',fontSize:13,fontWeight:700}}>
                <span className="material-symbols-outlined" style={{fontSize:18,fontVariationSettings:"'FILL' 1"}}>check_circle</span>
                Email updated successfully
              </div>
            )}
          </div>
        </div>

        <div style={{fontSize:12,fontWeight:800,color:'#1e293b',margin:'16px 0 4px'}}>Address</div>
        {row('Address', [child.addr, child.city, child.pin ? 'Pin-'+child.pin : ''].filter(Boolean).join(', '))}

        {/* Change Password */}
        <div style={{fontSize:12,fontWeight:800,color:'#1e293b',margin:'20px 0 4px'}}>Security</div>
        <div style={{display:'flex',gap:16,padding:'12px 0',alignItems:'flex-start',flexWrap:'wrap'}}>
          <div style={{fontSize:12,color:'#64748b',fontWeight:600,minWidth:140,paddingTop:2}}>Password</div>
          <div style={{flex:1,minWidth:200}}>
            {pwStep === 'idle' && (
              <button onClick={()=>setPwStep('form')}
                style={{fontSize:11,fontWeight:700,color:'#1960a3',background:'#eef4ff',border:'1px solid #c7d9f5',borderRadius:20,padding:'3px 12px',cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                <span className="material-symbols-outlined" style={{fontSize:13}}>lock_reset</span>
                Change Password
              </button>
            )}
            {pwStep === 'form' && (
              <div style={{maxWidth:340}}>
                {pwErr && <div style={{background:'#fef2f2',color:'#dc2626',borderRadius:8,padding:'7px 10px',fontSize:12,fontWeight:600,marginBottom:10,border:'1px solid #fecaca'}}>{pwErr}</div>}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#475569',marginBottom:5}}>Current Password</div>
                  <div style={{position:'relative'}}>
                    <input value={curPw} onChange={e=>{setCurPw(e.target.value);setPwErr('');}} type={showCur?'text':'password'} placeholder="Enter current password" style={{...inp,paddingRight:38}}/>
                    <button onClick={()=>setShowCur(v=>!v)} tabIndex={-1} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:2,display:'flex',alignItems:'center'}}>
                      <span className="material-symbols-outlined" style={{fontSize:16}}>{showCur?'visibility_off':'visibility'}</span>
                    </button>
                  </div>
                </div>
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#475569',marginBottom:5}}>New Password</div>
                  <div style={{position:'relative'}}>
                    <input value={newPw} onChange={e=>{setNewPw(e.target.value);setPwErr('');}} type={showNew?'text':'password'} placeholder="Min. 6 characters" style={{...inp,paddingRight:38}}/>
                    <button onClick={()=>setShowNew(v=>!v)} tabIndex={-1} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:2,display:'flex',alignItems:'center'}}>
                      <span className="material-symbols-outlined" style={{fontSize:16}}>{showNew?'visibility_off':'visibility'}</span>
                    </button>
                  </div>
                </div>
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#475569',marginBottom:5}}>Confirm New Password</div>
                  <input value={confPw} onChange={e=>{setConfPw(e.target.value);setPwErr('');}} type="password" placeholder="Re-enter new password" style={inp} onKeyDown={e=>e.key==='Enter'&&doChangePw()}/>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={doChangePw} disabled={pwBusy}
                    style={{flex:1,padding:'9px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',opacity:pwBusy?0.7:1}}>
                    {pwBusy ? 'Saving…' : 'Update Password'}
                  </button>
                  <button onClick={resetPw} style={{padding:'9px 14px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:600,fontSize:13,cursor:'pointer'}}>Cancel</button>
                </div>
              </div>
            )}
            {pwStep === 'done' && (
              <div style={{display:'flex',alignItems:'center',gap:8,color:'#059669',fontSize:13,fontWeight:700}}>
                <span className="material-symbols-outlined" style={{fontSize:18,fontVariationSettings:"'FILL' 1"}}>check_circle</span>
                Password updated successfully
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Passport Photo Cropper ────────────────────────────────────────
function PassportCropper({ src, onDone, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const startRef = useRef(null);

  // Passport ratio 3:4 (width:height), display at 270x360
  const CW = 270, CH = 360;

  const onImgLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target;
    setImgSize({ w, h });
    // Fit image to cover the crop box initially
    const s = Math.max(CW / w, CH / h);
    setScale(s);
    setPos({ x: (CW - w * s) / 2, y: (CH - h * s) / 2 });
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    setDrag(true);
    startRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  };
  const onMouseMove = (e) => {
    if (!drag || !startRef.current) return;
    setPos({ x: startRef.current.px + e.clientX - startRef.current.mx, y: startRef.current.py + e.clientY - startRef.current.my });
  };
  const onMouseUp = () => setDrag(false);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    setDrag(true);
    startRef.current = { mx: t.clientX, my: t.clientY, px: pos.x, py: pos.y };
  };
  const onTouchMove = (e) => {
    if (!drag || !startRef.current) return;
    const t = e.touches[0];
    setPos({ x: startRef.current.px + t.clientX - startRef.current.mx, y: startRef.current.py + t.clientY - startRef.current.my });
  };

  const onWheel = (e) => {
    e.preventDefault();
    setScale(s => Math.min(5, Math.max(0.2, s - e.deltaY * 0.001)));
  };

  const crop = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Output at 3:4 passport size (600x800px)
    canvas.width = 600; canvas.height = 800;
    const displayScale = 600 / CW;
    ctx.drawImage(imgRef.current, pos.x * displayScale, pos.y * displayScale, imgSize.w * scale * displayScale, imgSize.h * scale * displayScale);
    onDone(canvas.toDataURL('image/jpeg', 0.92));
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:20,padding:24,maxWidth:360,width:'100%'}}>
        <div style={{fontSize:15,fontWeight:800,color:'#1e293b',marginBottom:4,fontFamily:'Manrope,sans-serif'}}>Crop Passport Photo</div>
        <div style={{fontSize:12,color:'#94a3b8',marginBottom:16}}>Drag to reposition · Scroll/pinch to zoom · 3:4 passport ratio</div>

        {/* Crop box */}
        <div style={{position:'relative',width:CW,height:CH,overflow:'hidden',borderRadius:8,border:'2px solid #1960a3',cursor:drag?'grabbing':'grab',margin:'0 auto 16px',background:'#000',userSelect:'none'}}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onMouseUp}
          onWheel={onWheel}>
          <img ref={imgRef} src={src} alt="crop" draggable={false} onLoad={onImgLoad}
            style={{position:'absolute',left:pos.x,top:pos.y,width:imgSize.w*scale,height:imgSize.h*scale,maxWidth:'none',pointerEvents:'none'}}/>
          {/* Grid overlay */}
          <div style={{position:'absolute',inset:0,pointerEvents:'none',backgroundImage:'linear-gradient(rgba(255,255,255,0.15) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.15) 1px,transparent 1px)',backgroundSize:`${CW/3}px ${CH/3}px`}}/>
        </div>

        {/* Zoom slider */}
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
          <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8'}}>zoom_out</span>
          <input type="range" min="0.2" max="5" step="0.01" value={scale} onChange={e=>setScale(parseFloat(e.target.value))} style={{flex:1}}/>
          <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8'}}>zoom_in</span>
        </div>

        <canvas ref={canvasRef} style={{display:'none'}}/>

        <div style={{display:'flex',gap:10}}>
          <button onClick={onCancel} style={{flex:1,padding:'11px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
          <button onClick={crop} style={{flex:2,padding:'11px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#002045,#1960a3)',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            <span className="material-symbols-outlined" style={{fontSize:16}}>crop</span>Use This Photo
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Parent Documents ──────────────────────────────────────────────
const DOC_TYPES = [
  { id:'aadhar_student', label:"Student's Aadhaar Card (Both Sides)",        required:true,  hint:'PDF or image with front & back · Max 5MB' },
  { id:'aadhar_father',  label:"Father's Aadhaar Card (Both Sides)",         required:true,  hint:'PDF or image with front & back · Max 5MB' },
  { id:'aadhar_mother',  label:"Mother's Aadhaar Card (Both Sides)",         required:true,  hint:'PDF or image with front & back · Max 5MB' },
  { id:'photograph',     label:'Passport Photograph',                        required:true,  hint:'JPG/PNG · Clear face photo · Max 2MB' },
  { id:'birth_cert',     label:'Birth Certificate',                          required:false, hint:'PDF or image · Max 5MB' },
  { id:'family_photo',   label:'Group Photograph with Parents',              required:false, hint:'JPG/PNG · Max 5MB' },
  { id:'tc',             label:'Transfer Certificate (Previous School)',     required:false, hint:'PDF or image · Max 5MB' },
  { id:'char_cert',      label:'Character Certificate (Previous School)',    required:false, hint:'PDF or image · Max 5MB' },
  { id:'prev_rc',        label:'Report Card of Previous Class',              required:false, hint:'PDF or image · Max 5MB' },
];

function ParentDocuments({ child, activeSessionId, showHeader = true }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState({});
  const [cropState, setCropState] = useState(null); // { src, resolve }
  const sid = child.sid || child.id;

  const load = () => {
    setLoading(true);
    getMyDocuments()
      .then(r => setDocs(r.documents || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Auto-populate photograph from student profile photo on first load
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!loading) {
      const existingPhoto = docs.find(d => d.type === 'photograph');
      const profilePhoto = child.photo || '';
      // If no photograph doc yet but profile photo exists, auto-upload it
      if (!existingPhoto && profilePhoto && profilePhoto.startsWith('data:image')) {
        uploadMyDocument({ type: 'photograph', label: 'Passport Photograph', data: profilePhoto, mimeType: 'image/jpeg' })
          .then(() => load())
          .catch(() => {});
      }
    }
  }, [loading]);

  const getDoc = (type) => docs.find(d => d.type === type);

  // Canvas crop helper — returns a Promise<dataURL>
  const cropToPassport = (src) => new Promise(resolve => setCropState({ src, resolve }));

  const handleUpload = (type, label) => {
    const doc = getDoc(type);
    if (doc?.status === 'approved') return; // blocked — admin must reject first
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'photograph' ? 'image/*' : 'image/*,application/pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const maxMB = type === 'photograph' ? 5 : 5;
      if (file.size > maxMB * 1024 * 1024) { alert(`File too large. Max ${maxMB}MB.`); return; }

      if (type === 'photograph') {
        // Show crop modal for passport photo
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const cropped = await cropToPassport(ev.target.result);
          if (!cropped) return;
          setUploading(u => ({ ...u, [type]: true }));
          try {
            await uploadMyDocument({ type, label, data: cropped, mimeType: 'image/jpeg' });
            await load();
          } catch(e) { alert(e.message || 'Upload failed'); }
          finally { setUploading(u => ({ ...u, [type]: false })); }
        };
        reader.readAsDataURL(file);
      } else {
        setUploading(u => ({ ...u, [type]: true }));
        const reader = new FileReader();
        reader.onload = async (ev) => {
          try {
            await uploadMyDocument({ type, label, data: ev.target.result, mimeType: file.type });
            await load();
          } catch(e) { alert(e.message || 'Upload failed'); }
          finally { setUploading(u => ({ ...u, [type]: false })); }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleDownload = async (doc, label) => {
    setPreviewing(p => ({ ...p, [doc.id]: 'dl' }));
    try {
      const r = await getMyDocument(doc.id);
      const a = document.createElement('a');
      a.href = r.doc.data;
      a.download = label.replace(/[^a-z0-9]/gi, '_') + (r.doc.mimeType === 'application/pdf' ? '.pdf' : '.jpg');
      a.click();
    } catch(e) { alert('Failed to download'); }
    finally { setPreviewing(p => ({ ...p, [doc.id]: false })); }
  };

  const statusStyle = (status) => {
    if (status === 'approved') return { bg:'#dcfce7', color:'#059669', border:'#bbf7d0', icon:'check_circle', label:'Approved' };
    if (status === 'rejected') return { bg:'#fef2f2', color:'#dc2626', border:'#fecaca', icon:'cancel', label:'Rejected' };
    return { bg:'#fef9c3', color:'#b45309', border:'#fde68a', icon:'schedule', label:'Pending Review' };
  };

  return (
    <div style={{marginTop:24}}>
      <div style={{background:'#fff',borderRadius:16,padding:'20px 24px',boxShadow:'0 1px 8px rgba(0,31,77,0.06)'}}>
        <div style={{fontSize:12,fontWeight:800,color:'#1e293b',marginBottom:4,display:'flex',alignItems:'center',gap:8}}>
          <span className="material-symbols-outlined" style={{fontSize:16,color:'#1960a3',fontVariationSettings:"'FILL' 1"}}>folder_open</span>
          {showHeader && 'Documents'}
        </div>
        <div style={{fontSize:12,color:'#94a3b8',marginBottom:16}}>Upload required documents for school records. Each document will be reviewed by the school.</div>

        {loading ? (
          <div style={{textAlign:'center',padding:'24px',color:'#94a3b8',fontSize:13}}>Loading…</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {DOC_TYPES.map(dt => {
              const doc = getDoc(dt.id);
              const st = doc ? statusStyle(doc.status) : null;
              const busy = uploading[dt.id];
              return (
                <div key={dt.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:12,border:'1px solid #f1f5f9',background:'#fafbfc',flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:180}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#1e293b',display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                      {dt.label}
                      {dt.required && <span style={{fontSize:9,fontWeight:700,color:'#dc2626',background:'#fef2f2',padding:'1px 6px',borderRadius:10,border:'1px solid #fecaca'}}>Required</span>}
                    </div>
                    <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{dt.hint}</div>
                    {doc?.rejectReason && <div style={{fontSize:11,color:'#dc2626',marginTop:3,fontWeight:600}}>Reason: {doc.rejectReason}</div>}
                  </div>

                  {doc && (
                    <div style={{display:'flex',alignItems:'center',gap:6,background:st.bg,border:`1px solid ${st.border}`,borderRadius:20,padding:'3px 10px'}}>
                      <span className="material-symbols-outlined" style={{fontSize:13,color:st.color,fontVariationSettings:"'FILL' 1"}}>{st.icon}</span>
                      <span style={{fontSize:11,fontWeight:700,color:st.color}}>{st.label}</span>
                    </div>
                  )}

                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {doc && (
                      <>
                        <button onClick={async()=>{
                          setPreviewing(p=>({...p,[doc.id]:'view'}));
                          try {
                            const r = await getMyDocument(doc.id);
                            setPreview({label:dt.label, data:r.doc.data, mimeType:r.doc.mimeType});
                          } catch(e){ alert('Failed to load document'); }
                          finally { setPreviewing(p=>({...p,[doc.id]:false})); }
                        }}
                          style={{fontSize:11,fontWeight:700,color:'#1960a3',background:'#eef4ff',border:'1px solid #c7d9f5',borderRadius:8,padding:'5px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:3}}>
                          <span className="material-symbols-outlined" style={{fontSize:13}}>{previewing[doc.id]==='view'?'progress_activity':'visibility'}</span>
                          View
                        </button>
                        <button onClick={()=>handleDownload(doc, dt.label)}
                          style={{fontSize:11,fontWeight:700,color:'#059669',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'5px 10px',cursor:'pointer',display:'flex',alignItems:'center',gap:3}}>
                          <span className="material-symbols-outlined" style={{fontSize:13}}>{previewing[doc.id]==='dl'?'progress_activity':'download'}</span>
                          Save
                        </button>
                      </>
                    )}
                    {doc?.status !== 'approved' && (
                      <button onClick={()=>handleUpload(dt.id, dt.label)} disabled={busy}
                        style={{fontSize:11,fontWeight:700,color:'#fff',
                          background:doc?.status==='rejected'?'linear-gradient(135deg,#dc2626,#b91c1c)':'linear-gradient(135deg,#002045,#1960a3)',
                          border:'none',borderRadius:8,padding:'5px 10px',cursor:'pointer',
                          display:'flex',alignItems:'center',gap:3,opacity:busy?0.7:1}}>
                        <span className="material-symbols-outlined" style={{fontSize:13}}>{busy?'progress_activity':'upload'}</span>
                        {busy?'Uploading…':doc?'Re-upload':'Upload'}
                      </button>
                    )}
                    {doc?.status === 'approved' && (
                      <span style={{fontSize:11,fontWeight:700,color:'#94a3b8',padding:'5px 10px',display:'flex',alignItems:'center',gap:3}}>
                        <span className="material-symbols-outlined" style={{fontSize:13}}>lock</span>
                        Locked
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {preview && (
        <div onClick={()=>setPreview(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,padding:16,maxWidth:600,width:'100%',maxHeight:'90vh',overflow:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontSize:14,fontWeight:800,color:'#1e293b'}}>{preview.label}</div>
              <button onClick={()=>setPreview(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:20,lineHeight:1}}>✕</button>
            </div>
            {preview.mimeType?.startsWith('image') ? (
              <img src={preview.data} alt={preview.label} style={{width:'100%',borderRadius:8,display:'block'}}/>
            ) : (
              <iframe src={preview.data} style={{width:'100%',height:500,border:'none',borderRadius:8}}/>
            )}
          </div>
        </div>
      )}

      {/* Passport crop modal */}
      {cropState && <PassportCropper src={cropState.src} onDone={cropped=>{cropState.resolve(cropped);setCropState(null);}} onCancel={()=>{cropState.resolve(null);setCropState(null);}}/>}
    </div>
  );
}


// ── Parent Documents Page (standalone) ───────────────────────────
function ParentDocumentsPage({ child, activeSessionId }) {
  return (
    <div className="pp-fadein">
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>Student Documents</div>
        <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',margin:0}}>My Documents</h1>
      </div>
      <ParentDocuments child={child} activeSessionId={activeSessionId} showHeader={false} />
    </div>
  );
}

// ── Parent Dashboard ──────────────────────────────────────────────
function AttendanceDonut({ present, absent, late, size = 120 }) {
  const total = present + absent + late || 1;
  const r = 44; const cx = 60; const cy = 60;
  const circ = 2 * Math.PI * r;
  const pPct = present / total; const lPct = late / total; const aPct = absent / total;
  const pDash = circ * pPct; const lDash = circ * lPct; const aDash = circ * aPct;
  const pOffset = 0;
  const lOffset = -(circ * pPct);
  const aOffset = -(circ * (pPct + lPct));
  const attendedTotal = present + absent; // leave excluded
  const attPct = attendedTotal > 0 ? Math.round(present / attendedTotal * 100) : 0;
  const col = attPct >= 90 ? '#059669' : attPct >= 75 ? '#d97706' : '#dc2626';
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" style={{display:'block',flexShrink:0}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth="14"/>
      {present > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#059669" strokeWidth="14"
        strokeDasharray={`${pDash} ${circ - pDash}`} strokeDashoffset={pOffset}
        strokeLinecap="butt" transform="rotate(-90 60 60)" style={{transition:'stroke-dasharray 0.8s ease'}}/>}
      {late > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0891b2" strokeWidth="14"
        strokeDasharray={`${lDash} ${circ - lDash}`} strokeDashoffset={lOffset}
        strokeLinecap="butt" transform="rotate(-90 60 60)" style={{transition:'stroke-dasharray 0.8s ease'}}/>}
      {absent > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ef4444" strokeWidth="14"
        strokeDasharray={`${aDash} ${circ - aDash}`} strokeDashoffset={aOffset}
        strokeLinecap="butt" transform="rotate(-90 60 60)" style={{transition:'stroke-dasharray 0.8s ease'}}/>}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="900" fill={col} fontFamily="Manrope,sans-serif">{attPct}%</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fontWeight="700" fill="#94a3b8">Attendance</text>
    </svg>
  );
}

// ── Count-up hook ─────────────────────────────────────────────────
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = performance.now();
    const isNum = typeof target === 'number';
    if (!isNum) { setVal(target); return; }
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

// ── Parent Dashboard ──────────────────────────────────────────────
function ParentDash({ db, child, student, setPage }) {
  const sid = child.sid || child.id;
  const name = (child.fn || '') + ' ' + (child.ln || '');

  // Attendance summary
  let p = 0, a = 0, l = 0;
  Object.values(db.att || {}).forEach(d => {
    const v = d[sid];
    if (v === 'P') p++;
    else if (v === 'L') l++;
    else if (v === 'A') a++;
  });
  const tot = p + a + l;
  const attPct = (p + a) > 0 ? Math.round(p / (p + a) * 100) : 0;

  // Upcoming exams — real-time status
  const nowDash = new Date();
  const effectiveStatusDash = e => {
    if (e.dt && e.startTime && e.endTime) {
      const start = new Date(e.dt + 'T' + e.startTime);
      const end = new Date(e.dt + 'T' + e.endTime);
      if (!isNaN(start) && !isNaN(end)) {
        if (nowDash >= start && nowDash <= end) return 'Ongoing';
        if (nowDash > end) return 'Completed';
        return 'Upcoming';
      }
    } else if (e.dt) {
      const day = new Date(e.dt); day.setHours(23, 59, 59);
      return nowDash > day ? 'Completed' : 'Upcoming';
    }
    return e.st || 'Upcoming';
  };
  const today = nowDash.toISOString().split('T')[0];
  const allUpcoming = (db.exams || [])
    .filter(e => e.cls === child.cls && e.dt && effectiveStatusDash(e) === 'Upcoming')
    .sort((a, b) => (a.dt || '').localeCompare(b.dt || ''));
  const upcoming = allUpcoming.slice(0, 3); // display only 3, but count uses allUpcoming.length

  // Recent marks — use eid_cls key format
  const recentMarks = [];
  (db.exams || []).forEach(e => {
    const eid = e.eid || e.id;
    const cls = e.cls || '';
    const marksKey = cls ? `${eid}_${cls}` : eid;
    const examMarks = (db.marks || {})[marksKey] || (db.marks || {})[eid] || {};
    const studentMark = examMarks[sid];
    if (studentMark !== undefined && studentMark !== null) {
      recentMarks.push({ exam: e.name, marks: studentMark, max: e.max || 100 });
    }
  });

  // Fee summary
  const pays = (db.pays || []).filter(p => p.sid === sid);
  const totalPaid = pays.reduce((s, p) => s + (p.amt || 0), 0);
  const classFee = (db.settings?.classFees || {})[child.cls] || {};
  const monthlyFee = parseFloat(classFee.monthly) || parseFloat(child.mf) || (parseFloat(child.fee) > 0 ? Math.round(parseFloat(child.fee) / 12) : 0);
  const lastPay = pays.sort((a,b)=>(b.dt||'').localeCompare(a.dt||''))[0];
  const paidMonths = new Set();
  pays.forEach(p => { if (p.mn) p.mn.split(',').map(m=>m.trim()).filter(Boolean).forEach(m=>paidMonths.add(m.toLowerCase())); });
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const currentMonth = MONTHS[new Date().getMonth()];
  const currentMonthPaid = paidMonths.has(currentMonth.toLowerCase());
  const feeStatusLabel = currentMonthPaid ? 'Paid' : (child.fst === 'Paid' ? 'Paid' : 'Due');

  const attColor = attPct >= 90 ? '#059669' : attPct >= 75 ? '#d97706' : '#dc2626';
  const feeColor = feeStatusLabel === 'Paid' ? '#059669' : '#dc2626';

  const countExams = useCountUp(recentMarks.length);
  const countUpcoming = useCountUp(allUpcoming.length);

  const motivations = [
    'Keep up the great work!', 'Every day is a new opportunity to learn.',
    'Consistency is the key to success.', 'Stay focused and keep growing.',
  ];
  const motivation = motivations[Math.floor((new Date().getDate()) % motivations.length)];

  return (
    <div className="pp-fadein">
      {/* Welcome Banner */}
      <div className="pp-welcome-banner" style={{
        background: 'linear-gradient(135deg, #002045 0%, #1960a3 60%, #3b82f6 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        boxShadow: '0 8px 32px rgba(0,31,77,0.18)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{position:'absolute',top:-30,right:-30,width:180,height:180,borderRadius:'50%',background:'rgba(255,255,255,0.05)',pointerEvents:'none'}}/>
        <div style={{position:'absolute',bottom:-40,right:80,width:120,height:120,borderRadius:'50%',background:'rgba(255,255,255,0.04)',pointerEvents:'none'}}/>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.55)',textTransform:'uppercase',letterSpacing:'0.12em',marginBottom:6}}>Welcome back</div>
          <h1 style={{fontSize:26,fontWeight:900,color:'#fff',fontFamily:'Manrope,sans-serif',margin:'0 0 4px'}}>{name}</h1>
          <div style={{fontSize:13,color:'rgba(255,255,255,0.65)',marginBottom:10}}>Class {child.cls || '—'} · {db.settings?.year || ''}</div>
          <div style={{fontSize:13,color:'rgba(255,255,255,0.8)',fontStyle:'italic'}}>{motivation}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:20,flexWrap:'wrap'}}>
          {/* Donut chart */}
          <div className="pp-welcome-chart" style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
            <AttendanceDonut present={p} absent={a} late={l} size={110}/>
            <div style={{display:'flex',gap:10,flexWrap:'wrap',justifyContent:'center'}}>
              {[['#059669','Present',p],['#d97706','Leave',l],['#ef4444','Absent',a]].map(([c,lbl,v])=>(
                <div key={lbl} style={{display:'flex',alignItems:'center',gap:4}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:c,flexShrink:0}}/>
                  <span style={{fontSize:10,color:'rgba(255,255,255,0.65)',fontWeight:600}}>{lbl}: {v}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Fee mini-card */}
          <div style={{background:'rgba(255,255,255,0.1)',borderRadius:14,padding:'16px 20px',backdropFilter:'blur(8px)',border:'1px solid rgba(255,255,255,0.15)',minWidth:130}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.55)',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>This Month Fee</div>
            <div style={{fontSize:20,fontWeight:900,color: feeStatusLabel==='Paid'?'#6ee7b7':'#fca5a5',fontFamily:'Manrope,sans-serif',marginBottom:4}}>{feeStatusLabel}</div>
            {monthlyFee > 0 && <div style={{fontSize:11,color:'rgba(255,255,255,0.5)'}}>₹{monthlyFee.toLocaleString('en-IN')}/mo</div>}
            <div className="pp-pulse-badge" style={{display:'inline-block',marginTop:8,padding:'3px 10px',borderRadius:20,background: feeStatusLabel==='Paid'?'rgba(110,231,183,0.2)':'rgba(252,165,165,0.2)',border:`1px solid ${feeStatusLabel==='Paid'?'rgba(110,231,183,0.4)':'rgba(252,165,165,0.4)'}`,fontSize:10,fontWeight:700,color: feeStatusLabel==='Paid'?'#6ee7b7':'#fca5a5'}}>
              {feeStatusLabel === 'Paid' ? 'Cleared' : 'Due'}
            </div>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="pp-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { icon: 'fact_check', label: 'Attendance', rawVal: attPct, value: attPct + '%', color: attColor, page: 'patt' },
          { icon: 'payments', label: 'Fee Status', value: feeStatusLabel, color: feeColor, page: 'pfee' },
          { icon: 'quiz', label: 'Exams Taken', rawVal: recentMarks.length, value: countExams, color: '#1960a3', page: 'pmarks' },
          { icon: 'event_note', label: 'Upcoming', rawVal: allUpcoming.length, value: countUpcoming, color: '#7c3aed', page: 'pexam' },
        ].map(s => (
          <div key={s.label} className="pp-stat" onClick={() => s.page && setPage(s.page)}
            style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', cursor: s.page ? 'pointer' : 'default', border: '1px solid #f1f5f9' }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 19, color: s.color, fontVariationSettings:"'FILL' 1" }}>{s.icon}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: 'Manrope,sans-serif', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Bottom grid */}
      <div className="pp-dash-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Upcoming exams */}
        <div className="pp-card" style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', display:'flex', alignItems:'center', gap:6 }}>
              <span className="material-symbols-outlined" style={{fontSize:16,color:'#7c3aed',fontVariationSettings:"'FILL' 1"}}>event_note</span>
              Upcoming Exams
            </div>
            <button onClick={() => setPage('pexam')} style={{ fontSize: 11, color: '#1960a3', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>View all →</button>
          </div>
          {upcoming.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}><span className="material-symbols-outlined" style={{fontSize:32,color:'#e2e8f0',display:'block',margin:'0 auto 8px'}}>assignment</span></div>
              <div style={{ fontSize: 12 }}>No upcoming exams</div>
            </div>
          ) : upcoming.map(e => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 17, color: '#7c3aed', fontVariationSettings:"'FILL' 1" }}>quiz</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{e.su || ''}{e.su && e.dt ? ' · ' : ''}{e.dt || 'TBD'}</div>
              </div>
              <span style={{ padding: '3px 9px', borderRadius: 20, background: effectiveStatusDash(e) === 'Ongoing' ? '#fef3c7' : '#ede9fe', color: effectiveStatusDash(e) === 'Ongoing' ? '#d97706' : '#7c3aed', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{effectiveStatusDash(e) === 'Ongoing' ? '🔴 Now' : 'Soon'}</span>
            </div>
          ))}
        </div>

        {/* Recent results */}
        <div className="pp-card" style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}><span className="material-symbols-outlined" style={{fontSize:16,color:'#d97706',fontVariationSettings:"'FILL' 1",marginRight:6,verticalAlign:'middle'}}>emoji_events</span>Recent Results</div>
            <button onClick={() => setPage('pmarks')} style={{ fontSize: 11, color: '#1960a3', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>View all →</button>
          </div>
          {recentMarks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}><span className="material-symbols-outlined" style={{fontSize:32,color:'#e2e8f0',display:'block',margin:'0 auto 8px'}}>bar_chart</span></div>
              <div style={{ fontSize: 12 }}>No results yet</div>
            </div>
          ) : recentMarks.slice(-5).reverse().map((m, i) => {
            const pct = Math.round(m.marks / m.max * 100);
            const col = pct >= 75 ? '#059669' : pct >= 50 ? '#d97706' : '#dc2626';
            const g = pct >= 90 ? 'A+' : pct >= 80 ? 'A' : pct >= 70 ? 'B+' : pct >= 60 ? 'B' : pct >= 50 ? 'C' : 'F';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f8fafc' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.exam}</div>
                  <div style={{ height: 4, borderRadius: 4, background: '#f1f5f9', marginTop: 5, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: col, borderRadius: 4, transition: 'width 0.8s ease' }}/>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: col }}>{m.marks}/{m.max}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: col }}>{g}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Fee Breakdown Card (collapsible) ─────────────────────────────
function FeeBreakdownCard({ monthlyFee, academicMonths, pays, paidMonths, fmtAmt, fextras, paidExtrasMap, otherChargesPaid, otherChargesConcession, otherChargesRemaining }) {
  const [open, setOpen] = React.useState(false);

  // Summary counts for the header
  const paidCount = academicMonths.filter(m => paidMonths.has(m.toLowerCase())).length;
  const unpaidCount = academicMonths.length - paidCount;

  return (
    <div className="pp-card" style={{background:'#fff',borderRadius:16,boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9',marginBottom:24,overflow:'hidden'}}>
      {/* Header — always visible, click to toggle */}
      <button type="button" onClick={()=>setOpen(v=>!v)}
        style={{width:'100%',display:'flex',alignItems:'center',gap:12,padding:'16px 20px',background:'transparent',border:'none',cursor:'pointer',textAlign:'left'}}>
        <div style={{width:34,height:34,borderRadius:10,background:'#eef4ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
          <span className="material-symbols-outlined" style={{fontSize:17,color:'#1960a3',fontVariationSettings:"'FILL' 1"}}>account_balance_wallet</span>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:800,color:'#1e293b'}}>Monthly Fee Breakdown</div>
          <div style={{fontSize:11,color:'#94a3b8',marginTop:2,display:'flex',gap:10}}>
            {monthlyFee > 0 && <>
              <span style={{color:'#059669',fontWeight:600}}>{paidCount} paid</span>
              {unpaidCount > 0 && <span style={{color:'#dc2626',fontWeight:600}}>{unpaidCount} unpaid</span>}
            </>}
            {(fextras.length > 0 || otherChargesPaid > 0) && <span>· Other charges</span>}
          </div>
        </div>
        <span className="material-symbols-outlined" style={{fontSize:20,color:'#94a3b8',transition:'transform 220ms',transform:open?'rotate(180deg)':'none',flexShrink:0}}>expand_more</span>
      </button>

      {/* Expandable content */}
      {open && (
        <div style={{borderTop:'1px solid #f1f5f9',padding:'16px 20px',display:'flex',flexDirection:'column',gap:6}}>
          {monthlyFee > 0 ? (
            academicMonths.map(month => {
              const monthPays = pays.filter(p => p.mn && p.mn.split(',').map(m=>m.trim()).some(m=>m.toLowerCase()===month.toLowerCase()));
              const isPaid = paidMonths.has(month.toLowerCase());
              const paidAmt = monthPays.reduce((s,p) => {
                const mnCount = p.mn ? p.mn.split(',').filter(Boolean).length : 1;
                return s + (p.tuition||0) / mnCount;
              }, 0);
              const concAmt = monthPays.reduce((s,p) => {
                const mnCount = p.mn ? p.mn.split(',').filter(Boolean).length : 1;
                return s + (p.concession||0) / mnCount;
              }, 0);
              const netPayable = Math.max(0, monthlyFee - concAmt);
              const partialRemaining = isPaid && paidAmt < netPayable ? netPayable - paidAmt : 0;

              let statusColor, statusBg, statusLabel;
              if (isPaid && partialRemaining === 0) { statusColor='#059669'; statusBg='#f0fdf4'; statusLabel='Paid'; }
              else if (isPaid && partialRemaining > 0) { statusColor='#d97706'; statusBg='#fffbeb'; statusLabel='Partial'; }
              else { statusColor='#dc2626'; statusBg='#fef2f2'; statusLabel='Unpaid'; }

              return (
                <MonthRow key={month} month={month} monthlyFee={monthlyFee}
                  paidAmt={paidAmt} concAmt={concAmt} netPayable={netPayable}
                  partialRemaining={partialRemaining} remaining={isPaid?0:netPayable}
                  statusColor={statusColor} statusBg={statusBg} statusLabel={statusLabel}
                  isPaid={isPaid} fmtAmt={fmtAmt} monthPays={monthPays}
                />
              );
            })
          ) : (
            <div style={{textAlign:'center',padding:'16px',color:'#94a3b8',fontSize:13}}>No monthly fee configured for this class</div>
          )}

          {/* Other charges */}
          {(fextras.length > 0 || otherChargesPaid > 0) && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6}}>Other Charges</div>
              <div style={{background:'#f8fafc',borderRadius:10,overflow:'hidden'}}>
                {fextras.map((e, i) => {
                  const paid = paidExtrasMap[e.label] || 0;
                  const rem = Math.max(0, (parseFloat(e.amt)||0) - paid);
                  return (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',borderBottom: i < fextras.length-1 ? '1px solid #f1f5f9' : 'none'}}>
                      <div>
                        <div style={{fontSize:12,color:'#475569',fontWeight:700}}>{e.label}</div>
                        <div style={{fontSize:10,color:'#94a3b8',marginTop:1}}>Total: {fmtAmt(parseFloat(e.amt)||0)}</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        {paid > 0 && <div style={{fontSize:12,fontWeight:700,color:'#059669'}}>{fmtAmt(paid)} paid</div>}
                        {rem > 0
                          ? <div style={{fontSize:11,fontWeight:600,color:'#dc2626'}}>{fmtAmt(rem)} remaining</div>
                          : <div style={{fontSize:11,fontWeight:700,color:'#059669'}}>✓ Paid</div>}
                      </div>
                    </div>
                  );
                })}
                {otherChargesConcession > 0 && (
                  <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'#fffbeb',borderTop:'1px solid #f1f5f9'}}>
                    <span style={{fontSize:12,color:'#b45309',fontWeight:700}}>Charges Concession</span>
                    <span style={{fontSize:13,fontWeight:700,color:'#b45309'}}>− {fmtAmt(otherChargesConcession)}</span>
                  </div>
                )}
                {otherChargesRemaining > 0 && (
                  <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'#fef2f2',borderTop:'1px solid #f1f5f9'}}>
                    <span style={{fontSize:12,color:'#dc2626',fontWeight:700}}>Remaining</span>
                    <span style={{fontSize:13,fontWeight:800,color:'#dc2626'}}>{fmtAmt(otherChargesRemaining)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Month Row (collapsible) ───────────────────────────────────────
function MonthRow({ month, monthlyFee, paidAmt, concAmt, netPayable, partialRemaining, remaining, statusColor, statusBg, statusLabel, isPaid, fmtAmt, monthPays }) {
  const [open, setOpen] = React.useState(false);
  // Actual cash paid = net payable when fully paid, or paidAmt when partial
  const cashPaid = isPaid && partialRemaining === 0 ? netPayable : paidAmt;
  return (
    <div style={{borderRadius:10,overflow:'hidden',border:'1px solid #f1f5f9'}}>
      <button type="button" onClick={()=>setOpen(v=>!v)}
        style={{width:'100%',display:'flex',alignItems:'center',gap:12,padding:'11px 14px',background:open?'#f8fafc':'#fff',border:'none',cursor:'pointer',textAlign:'left',transition:'background 150ms'}}>
        <div style={{width:8,height:8,borderRadius:'50%',background:statusColor,flexShrink:0}}/>
        <span style={{flex:1,fontSize:13,fontWeight:700,color:'#1e293b'}}>{month}</span>
        <span style={{fontSize:11,fontWeight:700,padding:'2px 10px',borderRadius:20,background:statusBg,color:statusColor}}>{statusLabel}</span>
        {isPaid && <span style={{fontSize:12,fontWeight:600,color:'#059669'}}>{fmtAmt(netPayable)}</span>}
        {!isPaid && <span style={{fontSize:12,fontWeight:600,color:'#dc2626'}}>{fmtAmt(netPayable)}</span>}
        <span className="material-symbols-outlined" style={{fontSize:16,color:'#94a3b8',transition:'transform 200ms',transform:open?'rotate(180deg)':'none'}}>expand_more</span>
      </button>
      {open && (
        <div style={{background:'#f8fafc',borderTop:'1px solid #f1f5f9'}}>
          <div style={{display:'flex',justifyContent:'space-between',padding:'8px 14px',borderBottom:'1px solid #f1f5f9'}}>
            <span style={{fontSize:11,color:'#64748b'}}>Monthly Fee</span>
            <span style={{fontSize:12,fontWeight:600,color:'#1e293b'}}>{fmtAmt(monthlyFee)}</span>
          </div>
          {concAmt > 0 && (
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 14px',borderBottom:'1px solid #f1f5f9',background:'#f0fdf4'}}>
              <span style={{fontSize:11,color:'#059669',fontWeight:600}}>Concession</span>
              <span style={{fontSize:12,fontWeight:700,color:'#059669'}}>− {fmtAmt(concAmt)}</span>
            </div>
          )}
          <div style={{display:'flex',justifyContent:'space-between',padding:'8px 14px',borderBottom:'1px solid #f1f5f9',background:'#eef4ff'}}>
            <span style={{fontSize:11,color:'#1960a3',fontWeight:600}}>Net Payable</span>
            <span style={{fontSize:12,fontWeight:700,color:'#1960a3'}}>{fmtAmt(netPayable)}</span>
          </div>
          {isPaid && (
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 14px',borderBottom: partialRemaining > 0 ? '1px solid #f1f5f9' : 'none'}}>
              <span style={{fontSize:11,color:'#475569',fontWeight:600}}>Paid in Cash</span>
              <span style={{fontSize:12,fontWeight:700,color:'#059669'}}>{fmtAmt(cashPaid)}</span>
            </div>
          )}
          {partialRemaining > 0 && (
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 14px',background:'#fffbeb'}}>
              <span style={{fontSize:11,color:'#d97706',fontWeight:600}}>Still Remaining</span>
              <span style={{fontSize:12,fontWeight:700,color:'#d97706'}}>{fmtAmt(partialRemaining)}</span>
            </div>
          )}
          {!isPaid && (
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 14px',background:'#fef2f2'}}>
              <span style={{fontSize:11,color:'#dc2626',fontWeight:700}}>Due</span>
              <span style={{fontSize:12,fontWeight:800,color:'#dc2626'}}>{fmtAmt(netPayable)}</span>
            </div>
          )}
          {monthPays.length > 0 && (
            <div style={{padding:'6px 14px 8px',borderTop:'1px solid #f1f5f9'}}>
              {monthPays.map((p,i)=>(
                <div key={i} style={{fontSize:10,color:'#94a3b8',display:'flex',justifyContent:'space-between',marginTop:4}}>
                  <span>{p.dt} · {p.md||'Cash'}</span>
                  <span style={{fontWeight:600,color:'#64748b'}}>{p.rc||'—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Parent Fee Status ─────────────────────────────────────────────
function ParentFee({ db, child }) {
  const sid = child.sid || child.id;
  const pays = (db.pays || []).filter(p => p.sid === sid).sort((a,b)=>(b.dt||'').localeCompare(a.dt||''));
  const totalPaid = pays.reduce((s,p) => s + (p.amt||0), 0);
  const classFee = (db.settings?.classFees || {})[child.cls] || {};
  const monthlyFee = parseFloat(classFee.monthly) || parseFloat(child.mf) || 0;
  const examFee = parseFloat(classFee.exam) || 0;
  // Annual tuition = monthly × 12 + exam fee + other charges (fextras)
  // Other charges — declare fextras first so fextrasSum can use it
  const fextras = child.fextras || [];
  const fextrasSum = fextras.reduce((s, e) => s + (parseFloat(e.amt) || 0), 0);
  const annualTuition = monthlyFee > 0 ? monthlyFee * 12 + examFee : 0;
  const lastPay = pays[0];

  // Tuition calculations
  const tuitionPaidAmt = pays.reduce((s,p) => s + (p.tuition||0), 0);
  const tuitionConcession = pays.reduce((s,p) => s + (p.concession||0), 0);
  const effectiveTuition = Math.max(0, annualTuition - tuitionConcession);
  const tuitionRemaining = Math.max(0, effectiveTuition - tuitionPaidAmt);

  // Other charges calculations — from student's fextras + paidExtras
  const paidExtrasMap = child.paidExtras || {};
  const otherChargesTotal = fextras.reduce((s,e) => s + (parseFloat(e.amt)||0), 0);
  const otherChargesPaid = pays.reduce((s,p) => s + (p.extras||[]).reduce((a,e) => a + (parseFloat(e.amt)||0), 0), 0);
  const otherChargesConcession = pays.reduce((s,p) => s + (p.extrasConcession||0), 0);
  // Remaining = charges still in fextras (not yet fully paid)
  const otherChargesRemaining = fextras.reduce((s,e) => {
    const paid = paidExtrasMap[e.label] || 0;
    return s + Math.max(0, (parseFloat(e.amt)||0) - paid);
  }, 0);

  const paidMonths = new Set();
  pays.forEach(p => {
    if (p.mn) p.mn.split(',').map(m=>m.trim()).filter(Boolean).forEach(m=>paidMonths.add(m.toLowerCase()));
  });

  // Academic year months (April → March) — declared before use
  const academicMonths = ['April','May','June','July','August','September','October','November','December','January','February','March'];

  const now = new Date();
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const currentMonth = MONTHS[now.getMonth()];
  const nextMonth = MONTHS[(now.getMonth()+1)%12];
  const nextMonthDueDate = new Date(now.getFullYear(), now.getMonth()+1, 10);
  const nextMonthPaid = paidMonths.has(nextMonth.toLowerCase());

  // Find next unpaid month in academic year order (April → March)
  let dueMonth = null;
  let dueDateObj = null;
  for (const month of academicMonths) {
    if (!paidMonths.has(month.toLowerCase())) {
      dueMonth = month;
      const monthIdx = MONTHS.indexOf(month);
      // April–December = current year, January–March = next year
      const dueYear = monthIdx >= 3 ? now.getFullYear() : now.getFullYear() + 1;
      dueDateObj = new Date(dueYear, monthIdx, 10);
      break;
    }
  }
  const currentMonthPaid = paidMonths.has(currentMonth.toLowerCase());

  const feeStatus = currentMonthPaid ? 'Paid' : (child.fst === 'Paid' ? 'Paid' : 'Due');
  const statusColor = feeStatus === 'Paid' ? '#059669' : '#dc2626';

  const currentMonthIdx = now.getMonth();

  const grandRemaining = pays
    .filter(p => !p.isPrevBalancePay)
    .reduce((sum, p) => sum + (p.balanceRemaining || 0), 0);

  const fmtAmt = n => '₹' + (n||0).toLocaleString('en-IN');

  return (
    <div className="pp-fadein">
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:6}}>Fee Status</div>
        <h1 style={{fontSize:28,fontWeight:900,color:'#1e293b',fontFamily:'Manrope,sans-serif',margin:0}}>{child.fn} {child.ln}</h1>
        <div style={{fontSize:13,color:'#64748b',marginTop:4}}>Class {child.cls||'—'} · {db.settings?.year||''}</div>
      </div>

      {/* Summary cards */}
      <div className="pp-stats-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:24}}>
        {[
          {label:'Monthly Fee', value: monthlyFee ? fmtAmt(monthlyFee) : '—', color:'#1960a3', icon:'receipt_long'},
          {label:'Balance',     value: fmtAmt(grandRemaining), color: grandRemaining > 0 ? '#dc2626' : '#059669', icon: grandRemaining > 0 ? 'pending' : 'check_circle'},
          {label:'This Month',  value:feeStatus, color:statusColor, icon: feeStatus==='Paid'?'verified':'pending'},
        ].map(s=>(
          <div key={s.label} className="pp-stat" style={{background:'#fff',borderRadius:16,padding:'20px',boxShadow:'0 2px 12px rgba(0,31,77,0.07)',border:'1px solid #f1f5f9'}}>
            <div style={{width:38,height:38,borderRadius:11,background:s.color+'18',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12}}>
              <span className="material-symbols-outlined" style={{fontSize:19,color:s.color,fontVariationSettings:"'FILL' 1"}}>{s.icon}</span>
            </div>
            <div style={{fontSize:22,fontWeight:900,color:s.color,fontFamily:'Manrope,sans-serif'}}>{s.value}</div>
            <div style={{fontSize:11,color:'#64748b',marginTop:4,fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Detailed Fee Breakdown — Month by Month */}
      <FeeBreakdownCard
        monthlyFee={monthlyFee} academicMonths={academicMonths} pays={pays}
        paidMonths={paidMonths} fmtAmt={fmtAmt} fextras={fextras}
        paidExtrasMap={paidExtrasMap} otherChargesPaid={otherChargesPaid}
        otherChargesConcession={otherChargesConcession} otherChargesRemaining={otherChargesRemaining}
      />

      {/* Next month due */}
      {monthlyFee > 0 && (
        <div style={{background:'linear-gradient(135deg,#002045,#1960a3)',borderRadius:16,padding:'20px 24px',marginBottom:24,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <div style={{width:44,height:44,borderRadius:12,background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <span className="material-symbols-outlined" style={{fontSize:22,color:'#fff',fontVariationSettings:"'FILL' 1"}}>event_upcoming</span>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.55)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2}}>Next Month Due</div>
            {dueMonth ? <>
              <div style={{fontSize:20,fontWeight:900,color:'#fff',fontFamily:'Manrope,sans-serif'}}>₹{monthlyFee.toLocaleString('en-IN')} · {dueMonth}</div>
              <div style={{fontSize:12,color:'rgba(255,255,255,0.6)',marginTop:2}}>
                Due by {dueDateObj.toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}
              </div>
            </> : <div style={{fontSize:16,fontWeight:700,color:'#6ee7b7'}}>All months paid</div>}
          </div>
          {lastPay && (
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.45)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2}}>Last Payment</div>
              <div style={{fontSize:16,fontWeight:800,color:'#fff'}}>₹{(lastPay.amt||0).toLocaleString('en-IN')}</div>
              <div style={{fontSize:11,color:'rgba(255,255,255,0.55)'}}>
                {lastPay.dt ? new Date(lastPay.dt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                {lastPay.mn ? ` · ${lastPay.mn}` : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment history */}
      <div className="pp-card" style={{background:'#fff',borderRadius:16,boxShadow:'0 2px 12px rgba(0,31,77,0.07)',overflow:'hidden',border:'1px solid #f1f5f9'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{fontSize:13,fontWeight:700,color:'#1e293b'}}>Payment History ({pays.length})</div>
          <div style={{fontSize:11,color:'#94a3b8',fontWeight:600}}>₹{totalPaid.toLocaleString('en-IN')} total paid</div>
        </div>
        {pays.length === 0 ? (
          <div style={{padding:'48px',textAlign:'center',color:'#94a3b8'}}>
            <span className="material-symbols-outlined" style={{fontSize:32,color:'#e2e8f0',display:'block',margin:'0 auto 8px'}}>credit_card</span>
            <div style={{fontSize:13}}>No payments recorded yet</div>
          </div>
        ) : (
          <div>
            {pays.map((p,i)=>{
              const hasTuition = p.tuition > 0;
              const hasExtras = (p.extras||[]).length > 0;
              const hasExamFee = p.examFee > 0;
              const hasConcession = p.concession > 0 || p.extrasConcession > 0;
              const hasRemaining = (p.tuitionRemaining||0) + (p.extrasRemaining||0) > 0;
              const totalRemaining = (p.tuitionRemaining||0) + (p.extrasRemaining||0);

              const printReceipt = () => {
                const sch = db.settings?.school || 'LORD KRISHNA PUBLIC SCHOOL';
                const addr = db.settings?.addr || '';
                const phone = db.settings?.phone || '';
                const fmt = n => '\u20B9' + (n||0).toLocaleString('en-IN');
                const full = !hasRemaining;
                const totalRem = (p.tuitionRemaining||0) + (p.extrasRemaining||0);

                const tuitionRow = hasTuition
                  ? `<tr><td>Tuition${p.mn?' \u2014 '+p.mn:''}</td><td class="amt">${fmt(p.tuition)}</td></tr>${p.tuitionRemaining>0?`<tr class="sub-row"><td>\u21B3 Remaining</td><td class="amt">${fmt(p.tuitionRemaining)}</td></tr>`:''}` : '';
                const extrasRows = (p.extras||[]).map(e=>{
                  const rem = e.remaining>0?`<tr class="sub-row"><td>\u21B3 Remaining</td><td class="amt">${fmt(e.remaining)}</td></tr>`:'';
                  return `<tr><td>${e.label}</td><td class="amt">${fmt(parseFloat(e.amt)||0)}</td></tr>${rem}`;
                }).join('');
                const concRow = p.concession>0?`<tr class="disc-row"><td>Concession</td><td class="amt">\u2212 ${fmt(p.concession)}</td></tr>`:'';
                const extrasConcRow = p.extrasConcession>0?`<tr class="disc-row"><td>Charges Concession</td><td class="amt">\u2212 ${fmt(p.extrasConcession)}</td></tr>`:'';
                const examFeeRow = p.examFee>0?`<tr><td>Exam Fee</td><td class="amt">${fmt(p.examFee)}</td></tr>`:'';
                const noteRow = p.note?`<tr><td colspan="2" class="note">Note: ${p.note}</td></tr>`:'';

                const css = `
                  @page{size:80mm auto;margin:0}
                  *{box-sizing:border-box;margin:0;padding:0}
                  body{font-family:'Courier New',Courier,monospace;font-size:11px;color:#000;background:#fff;width:72mm;padding:4mm 3mm 6mm}
                  .hdr{text-align:center;margin-bottom:6px}
                  .sch-name{font-size:13px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;line-height:1.3}
                  .sch-addr{font-size:9px;color:#333;margin-top:2px;line-height:1.4}
                  .sch-phone{font-size:9px;color:#333}
                  .divider{border:none;border-top:1px dashed #000;margin:5px 0}
                  .divider-double{border:none;border-top:3px double #000;margin:5px 0}
                  .receipt-title{text-align:center;font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin:4px 0}
                  .info-table{width:100%;border-collapse:collapse;margin-bottom:4px}
                  .info-table td{font-size:10px;padding:1.5px 0;vertical-align:top}
                  .info-table td.lbl{width:36%;color:#444}
                  .info-table td.sep{width:4%;text-align:center}
                  .info-table td.val{font-weight:600}
                  .charges{width:100%;border-collapse:collapse;margin:4px 0}
                  .charges thead tr td{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;padding:2px 0;border-top:1px dashed #000;border-bottom:1px dashed #000}
                  .charges thead tr td.amt{text-align:right}
                  .charges tbody tr td{font-size:10px;padding:2px 0}
                  .charges tbody tr td.amt{text-align:right;font-weight:600}
                  .sub-row td{font-size:9px;padding-left:8px;color:#444}
                  .disc-row td{font-size:10px}
                  .note{font-size:9px;color:#555;padding-top:2px}
                  .total-box{border:1px solid #000;padding:4px 6px;margin:5px 0 3px;display:flex;justify-content:space-between;align-items:center}
                  .total-lbl{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px}
                  .total-val{font-size:14px;font-weight:700}
                  .balance-row{display:flex;justify-content:space-between;font-size:10px;font-weight:600;padding:2px 0}
                  .remaining-row{display:flex;justify-content:space-between;font-size:10px;font-weight:600;padding:4px 0 2px;border-top:1px dashed #000;margin-top:3px}
                  .stamp-wrap{text-align:center;margin:6px 0 4px}
                  .stamp{display:inline-block;border:2px solid #000;font-size:13px;font-weight:700;letter-spacing:3px;padding:3px 16px;transform:rotate(-4deg);text-transform:uppercase}
                  .footer{text-align:center;font-size:8px;color:#555;margin-top:6px;line-height:1.5}
                  .footer .thank{font-size:10px;font-weight:700;margin-bottom:2px}
                  @media print{@page{size:80mm auto;margin:0}body{padding:3mm 3mm 8mm}}
                `;

                const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Receipt ${p.rc||''}</title><style>${css}</style></head>
<body>
  <div class="hdr">
    <div class="sch-name">${sch}</div>
    ${addr?`<div class="sch-addr">${addr}</div>`:''}
    ${phone?`<div class="sch-phone">Ph: ${phone}</div>`:''}
  </div>
  <hr class="divider-double">
  <div class="receipt-title">Fee Payment Receipt</div>
  <hr class="divider">
  <table class="info-table">
    <tr><td class="lbl">Receipt No.</td><td class="sep">:</td><td class="val">${p.rc||'—'}</td></tr>
    <tr><td class="lbl">Date</td><td class="sep">:</td><td class="val">${p.dt||'—'}</td></tr>
    <tr><td class="lbl">Student</td><td class="sep">:</td><td class="val">${child.fn} ${child.ln}</td></tr>
    <tr><td class="lbl">Class / Roll</td><td class="sep">:</td><td class="val">${child.cls||'—'} / ${child.roll||'—'}</td></tr>
    <tr><td class="lbl">Adm. No.</td><td class="sep">:</td><td class="val">${child.admno||child.an||'—'}</td></tr>
    <tr><td class="lbl">Father</td><td class="sep">:</td><td class="val">${child.father||child.fa||'—'}</td></tr>
    <tr><td class="lbl">Method</td><td class="sep">:</td><td class="val">${p.md||p.mode||'Cash'}${p.ref?' ('+p.ref+')':''}</td></tr>
  </table>
  <hr class="divider">
  <table class="charges">
    <thead><tr><td>Description</td><td class="amt">Amount</td></tr></thead>
    <tbody>${tuitionRow}${extrasRows}${concRow}${extrasConcRow}${examFeeRow}${noteRow}</tbody>
  </table>
  <hr class="divider">
  <div class="total-box">
    <span class="total-lbl">Total Paid</span>
    <span class="total-val">${fmt(p.amt)}</span>
  </div>
  <div class="balance-row"><span>Balance</span><span>${fmt(p.ba||0)}</span></div>
  ${totalRem>0?`<div class="remaining-row"><span>Total Remaining</span><span>${fmt(totalRem)}</span></div>`:''}
  <hr class="divider">
  <div class="footer">
    <div class="thank">Thank You!</div>
    <div>This is a computer generated receipt.</div>
    <div>${sch}</div>
  </div>
  <script>window.onload=()=>{window.focus();window.print();}<\/script>
</body></html>`;

                const w = window.open('','_blank','width=320,height=600');
                if (w) { w.document.write(html); w.document.close(); }
              };

              return (
                <div key={p.id||i} style={{borderBottom:'1px solid #f1f5f9',padding:'16px 20px'}}>
                  {/* Header row */}
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap',marginBottom: (hasTuition||hasExtras||hasExamFee) ? 10 : 0}}>
                    <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
                      <div style={{width:38,height:38,borderRadius:10,background:'#f0fdf4',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <span className="material-symbols-outlined" style={{fontSize:18,color:'#059669',fontVariationSettings:"'FILL' 1"}}>payments</span>
                      </div>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:'#1e293b'}}>₹{(p.amt||0).toLocaleString('en-IN')}</div>
                        <div style={{fontSize:11,color:'#64748b',marginTop:2}}>
                          {p.dt ? new Date(p.dt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                          {p.mn ? <span style={{marginLeft:6,background:'#eef4ff',color:'#1960a3',padding:'1px 7px',borderRadius:10,fontSize:10,fontWeight:700}}>{p.mn}</span> : null}
                        </div>
                        <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>
                          {p.md||p.mode||'Cash'}{p.ref ? ` · ${p.ref}` : ''} · {p.rc||'—'}
                        </div>
                      </div>
                    </div>
                    <button onClick={printReceipt}
                      style={{display:'flex',alignItems:'center',gap:5,padding:'6px 12px',borderRadius:8,border:'1.5px solid #c7d9f5',background:'#eef4ff',color:'#1960a3',fontWeight:700,fontSize:11,cursor:'pointer',flexShrink:0}}>
                      <span className="material-symbols-outlined" style={{fontSize:14}}>print</span>
                      Receipt
                    </button>
                  </div>

                  {/* Charges breakdown */}
                  {(hasTuition || hasExtras || hasExamFee) && (
                    <div style={{background:'#f8fafc',borderRadius:10,padding:'10px 14px',fontSize:12}}>
                      {hasTuition && (
                        <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:(hasExtras||hasConcession||hasExamFee)?'1px solid #f1f5f9':'none'}}>
                          <span style={{color:'#475569'}}>Tuition{p.mn ? ` — ${p.mn}` : ''}</span>
                          <span style={{fontWeight:600,color:'#1e293b'}}>₹{(p.tuition||0).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {p.tuitionRemaining > 0 && (
                        <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0 3px 12px',borderBottom:(hasExtras||hasConcession||hasExamFee)?'1px solid #f1f5f9':'none'}}>
                          <span style={{color:'#b45309',fontSize:11}}>↳ Tuition Remaining</span>
                          <span style={{fontWeight:600,color:'#b45309',fontSize:11}}>₹{p.tuitionRemaining.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {(p.extras||[]).map((e,ei)=>(
                        <div key={ei}>
                          <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:'1px solid #f1f5f9'}}>
                            <span style={{color:'#475569'}}>{e.label}</span>
                            <span style={{fontWeight:600,color:'#1e293b'}}>₹{(parseFloat(e.amt)||0).toLocaleString('en-IN')}</span>
                          </div>
                          {e.remaining > 0 && (
                            <div style={{display:'flex',justifyContent:'space-between',padding:'2px 0 3px 12px',borderBottom:'1px solid #f1f5f9'}}>
                              <span style={{color:'#b45309',fontSize:11}}>↳ Remaining</span>
                              <span style={{fontWeight:600,color:'#b45309',fontSize:11}}>₹{e.remaining.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {p.extrasConcession > 0 && (
                        <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:'1px solid #f1f5f9'}}>
                          <span style={{color:'#b45309'}}>Charges Concession</span>
                          <span style={{fontWeight:600,color:'#b45309'}}>− ₹{p.extrasConcession.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {p.concession > 0 && (
                        <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:'1px solid #f1f5f9'}}>
                          <span style={{color:'#059669'}}>Concession</span>
                          <span style={{fontWeight:600,color:'#059669'}}>− ₹{p.concession.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      {hasExamFee && (
                        <div style={{display:'flex',justifyContent:'space-between',padding:'3px 0',borderBottom:'1px solid #f1f5f9',color:'#7c3aed'}}>
                          <span style={{fontWeight:600}}>Examination Fee <span style={{fontSize:10,opacity:0.7}}>(one-time)</span></span>
                          <span style={{fontWeight:600}}>₹{(p.examFee||0).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0 0',marginTop:2}}>
                        <span style={{fontWeight:700,color:'#1e293b'}}>Total Paid</span>
                        <span style={{fontWeight:800,color:'#059669',fontSize:13}}>₹{(p.amt||0).toLocaleString('en-IN')}</span>
                      </div>
                      {totalRemaining > 0 && (
                        <div style={{display:'flex',justifyContent:'space-between',padding:'4px 0 0',borderTop:'1px dashed #e2e8f0',marginTop:4}}>
                          <span style={{fontWeight:600,color:'#b45309',fontSize:11}}>Total Remaining</span>
                          <span style={{fontWeight:700,color:'#b45309',fontSize:12}}>₹{totalRemaining.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {p.note && <div style={{fontSize:11,color:'#94a3b8',marginTop:6}}>Note: {p.note}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Parent Attendance ─────────────────────────────────────────────
function ParentAttendance({ db, child }) {
  const sid = child.sid || child.id;
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const FULL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calOpen, setCalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const now = new Date();

  let p = 0, a = 0, l = 0;
  const history = [];
  const monthlyData = {};
  const attMap = {};
  Object.entries(db.att || {}).forEach(([date, d]) => {
    const v = d[sid];
    if (v === 'P') p++;
    else if (v === 'L') l++;
    else if (v === 'A') a++;
    if (v) {
      attMap[date] = v;
      history.push({ date, status: v });
      const mo = new Date(date).getMonth();
      if (!monthlyData[mo]) monthlyData[mo] = { p: 0, a: 0, l: 0 };
      if (v === 'P') monthlyData[mo].p++;
      else if (v === 'A') monthlyData[mo].a++;
      else if (v === 'L') monthlyData[mo].l++;
    }
  });
  history.sort((a, b) => b.date.localeCompare(a.date));
  const tot = p + a + l;
  const attendedDays = p + a; // leave excluded from calculation
  const pct = attendedDays > 0 ? Math.round(p / attendedDays * 100) : 0;
  const col = pct >= 90 ? '#059669' : pct >= 75 ? '#d97706' : '#dc2626';

  const chartMonths = Object.keys(monthlyData).map(Number).sort((a,b)=>a-b).slice(-6);

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y=>y-1); } else setCalMonth(m=>m-1); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y=>y+1); } else setCalMonth(m=>m+1); };

  // Build calendar grid for selected month
  const firstDay = new Date(calYear, calMonth, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const calCells = [];
  for (let i = 0; i < firstDay; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);

  const dateStr = (d) => `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const statusOf = (d) => d ? attMap[dateStr(d)] : null;

  const cellStyle = (d) => {
    const s = statusOf(d);
    const isToday = d && new Date().toISOString().split('T')[0] === dateStr(d);
    if (!d) return { background:'transparent', border:'none' };
    if (s === 'P') return { background:'#dcfce7', border:'1.5px solid #86efac', color:'#059669' };
    if (s === 'A') return { background:'#fee2e2', border:'1.5px solid #fca5a5', color:'#dc2626' };
    if (s === 'L') return { background:'#fef3c7', border:'1.5px solid #7dd3fc', color:'#d97706' };
    if (isToday) return { background:'#eef4ff', border:'1.5px solid #93c5fd', color:'#1960a3' };
    return { background:'#f8fafc', border:'1px solid #f1f5f9', color:'#94a3b8' };
  };

  const statusLabel = (s) => s === 'P' ? 'P' : s === 'A' ? 'A' : s === 'L' ? 'L' : '';

  // Monthly summary for selected month
  const mData = monthlyData[calMonth] || { p:0, a:0, l:0 };
  const mTotal = mData.p + mData.a + mData.l;
  const mPct = mTotal > 0 ? Math.round((mData.p + mData.l) / mTotal * 100) : null;

  return (
    <div className="pp-fadein">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#1e293b', fontFamily: 'Manrope,sans-serif', margin:0 }}>Attendance</h1>
        <button onClick={()=>setCalOpen(true)}
          style={{display:'flex',alignItems:'center',gap:6,padding:'9px 16px',borderRadius:12,border:'1.5px solid #c7d9f5',background:'#eef4ff',color:'#1960a3',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>
          <span className="material-symbols-outlined" style={{fontSize:18,fontVariationSettings:"'FILL' 1"}}>calendar_month</span>
          Calendar
        </button>
      </div>

      {/* Stat cards */}
      <div className="pp-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        {[{ label: 'Present', val: p, c: '#059669', icon: 'check_circle' },
          { label: 'Absent', val: a, c: '#dc2626', icon: 'cancel' },
          { label: 'Leave', val: l, c: '#d97706', icon: 'event_available' },
          { label: 'Attendance %', val: pct + '%', c: col, icon: 'fact_check' }].map(s => (
          <div key={s.label} className="pp-stat" style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', textAlign: 'center' }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: s.c + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 19, color: s.c, fontVariationSettings:"'FILL' 1" }}>{s.icon}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.c, fontFamily: 'Manrope,sans-serif' }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Overall progress bar */}
      <div className="pp-card" style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Overall Attendance</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: col, fontFamily: 'Manrope,sans-serif' }}>{pct}%</div>
        </div>
        <div style={{ height: 10, borderRadius: 10, background: '#f1f5f9', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: pct + '%', background: `linear-gradient(90deg, ${col}, ${col}cc)`, borderRadius: 10, transition: 'width 1s cubic-bezier(0.4,0,0.2,1)' }}/>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>0%</span>
          <span style={{ fontSize: 10, color: pct >= 75 ? '#059669' : '#dc2626', fontWeight: 700 }}>
            {pct >= 90 ? 'Excellent' : pct >= 75 ? 'Satisfactory' : 'Below Required (75%)'}
          </span>
          <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>100%</span>
        </div>
      </div>

      {/* Monthly bar chart */}
      {chartMonths.length > 0 && (
        <div className="pp-card" style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,31,77,0.07)', border: '1px solid #f1f5f9', marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16 }}>Monthly Attendance</div>
        <div className="pp-bar-chart" style={{ overflowX: 'auto' }}>
          <svg width={Math.max(chartMonths.length * 70, 300)} height={130} viewBox={`0 0 ${Math.max(chartMonths.length * 70, 300)} 130`} style={{ display: 'block', overflow: 'hidden' }}>
            {chartMonths.map((mo, i) => {
              const d = monthlyData[mo] || { p: 0, a: 0, l: 0 };
              const total = d.p + d.a + d.l;
              const attPctM = (d.p + d.a) > 0 ? d.p / (d.p + d.a) : 0;
              const barH = Math.round(attPctM * 72);
              const barColor = attPctM >= 0.9 ? '#059669' : attPctM >= 0.75 ? '#d97706' : '#ef4444';
              const x = i * 70 + 10;
              return (
                <g key={mo} onClick={()=>{setCalMonth(mo);setCalYear(now.getFullYear());}} style={{cursor:'pointer'}}>
                  <rect x={x} y={90 - barH} width={50} height={barH} rx={6} fill={barColor} opacity={calMonth===mo?1:0.7}/>
                  <text x={x + 25} y={108} textAnchor="middle" fontSize="9" fontWeight="700" fill="#64748b">{MONTHS[mo]}</text>
                  <text x={x + 25} y={Math.max(90 - barH - 4, 12)} textAnchor="middle" fontSize="9" fontWeight="800" fill={barColor}>{Math.round(attPctM * 100)}%</text>
                </g>
              );
            })}
            <line x1="0" y1="90" x2={Math.max(chartMonths.length * 70, 300)} y2="90" stroke="#f1f5f9" strokeWidth="1"/>
          </svg>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Tap a bar to jump to that month's calendar</div>
        </div>
      )}

      {/* Full history table */}
      <div className="pp-card" style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,31,77,0.07)', overflow: 'hidden', border: '1px solid #f1f5f9' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Attendance History</div>
        {history.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{fontSize:32,color:'#e2e8f0',display:'block',margin:'0 auto 8px'}}>assignment</span>
            <div style={{ fontSize: 13 }}>No attendance records yet</div>
          </div>
        ) : (
          <div className="parent-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Date', 'Day', 'Status'].map(h => <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc' }}>{h}</th>)}</tr></thead>
              <tbody>{history.slice(0, 90).map((r, i) => {
                const sc = r.status === 'P' ? '#059669' : r.status === 'A' ? '#dc2626' : '#d97706';
                const sl = r.status === 'P' ? 'Present' : r.status === 'A' ? 'Absent' : 'Leave';
                const dt = new Date(r.date);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 20px', fontSize: 13, color: '#1e293b' }}>{dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td style={{ padding: '10px 20px', fontSize: 12, color: '#64748b' }}>{dt.toLocaleDateString('en-IN', { weekday: 'short' })}</td>
                    <td style={{ padding: '10px 20px' }}><span style={{ padding: '3px 10px', borderRadius: 20, background: sc + '18', color: sc, fontSize: 11, fontWeight: 700 }}>{sl}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </div>

      {/* Calendar Modal */}
      {calOpen && (
        <div onClick={()=>{setCalOpen(false);setSelectedDate(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:20,padding:24,width:'100%',maxWidth:380,boxShadow:'0 24px 60px rgba(0,0,0,0.25)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <button onClick={prevMonth} style={{width:32,height:32,borderRadius:8,border:'1px solid #e2e8f0',background:'#f8fafc',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#64748b'}}>
                <span className="material-symbols-outlined" style={{fontSize:18}}>chevron_left</span>
              </button>
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:15,fontWeight:800,color:'#1e293b',fontFamily:'Manrope,sans-serif'}}>{FULL_MONTHS[calMonth]} {calYear}</div>
                {(() => { const md = monthlyData[calMonth]||{p:0,a:0,l:0}; const mt = md.p+md.a+md.l; const mp = mt>0?Math.round((md.p+md.l)/mt*100):null;
                  return mp!==null ? <div style={{fontSize:11,color:mp>=75?'#059669':'#dc2626',fontWeight:700,marginTop:2}}>{md.p}P · {md.a}A · {md.l}L · {mp}%</div> : null;
                })()}
              </div>
              <button onClick={nextMonth} style={{width:32,height:32,borderRadius:8,border:'1px solid #e2e8f0',background:'#f8fafc',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#64748b'}}>
                <span className="material-symbols-outlined" style={{fontSize:18}}>chevron_right</span>
              </button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3,marginBottom:3}}>
              {['S','M','T','W','T','F','S'].map((d,i) => (
                <div key={i} style={{textAlign:'center',fontSize:10,fontWeight:800,color:'#94a3b8',padding:'3px 0'}}>{d}</div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3}}>
              {calCells.map((d, i) => {
                const cs = cellStyle(d);
                const s = statusOf(d);
                const ds = d ? dateStr(d) : null;
                const isSelected = ds && selectedDate === ds;
                return (
                  <div key={i} onClick={()=>d&&setSelectedDate(ds===selectedDate?null:ds)}
                    style={{aspectRatio:'1',borderRadius:8,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',cursor:d?'pointer':'default',minHeight:38,
                      background: isSelected ? '#1960a3' : cs.background,
                      border: isSelected ? '2px solid #1960a3' : cs.border,
                      color: isSelected ? '#fff' : cs.color,
                      transform: isSelected ? 'scale(1.1)' : 'none',
                      transition:'all 150ms',
                    }}>
                    {d && <>
                      <span style={{fontSize:13,fontWeight:700,lineHeight:1}}>{d}</span>
                      {s && <span style={{fontSize:8,fontWeight:800,lineHeight:1,marginTop:1}}>{statusLabel(s)}</span>}
                    </>}
                  </div>
                );
              })}
            </div>
            {selectedDate && (()=>{
              const s = attMap[selectedDate];
              const dt = new Date(selectedDate);
              const dayName = dt.toLocaleDateString('en-IN',{weekday:'long'});
              const fullDate = dt.toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'});
              const sc = s==='P'?'#059669':s==='A'?'#dc2626':s==='L'?'#0891b2':'#94a3b8';
              const bg = s==='P'?'#dcfce7':s==='A'?'#fee2e2':s==='L'?'#e0f2fe':'#f8fafc';
              const label = s==='P'?'Present':s==='A'?'Absent':s==='L'?'Leave':'No Record';
              const icon = s==='P'?'check_circle':s==='A'?'cancel':s==='L'?'event_available':'event_busy';
              return (
                <div style={{marginTop:14,padding:'14px 16px',borderRadius:12,background:bg,border:`1.5px solid ${sc}33`,display:'flex',alignItems:'center',gap:12}}>
                  <span className="material-symbols-outlined" style={{fontSize:28,color:sc,fontVariationSettings:"'FILL' 1",flexShrink:0}}>{icon}</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:'#1e293b'}}>{fullDate}</div>
                    <div style={{fontSize:11,color:'#64748b',marginTop:1}}>{dayName}</div>
                    <div style={{fontSize:14,fontWeight:800,color:sc,marginTop:4}}>{label}</div>
                  </div>
                </div>
              );
            })()}
            <div style={{display:'flex',gap:10,marginTop:14,flexWrap:'wrap',justifyContent:'center'}}>
              {[['#059669','#dcfce7','Present'],['#dc2626','#fee2e2','Absent'],['#d97706','#fef3c7','Leave']].map(([c,bg,lbl])=>(
                <div key={lbl} style={{display:'flex',alignItems:'center',gap:4}}>
                  <div style={{width:10,height:10,borderRadius:3,background:bg,border:`1.5px solid ${c}55`}}/>
                  <span style={{fontSize:10,color:'#64748b',fontWeight:600}}>{lbl}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>{setCalOpen(false);setSelectedDate(null);}}
              style={{width:'100%',marginTop:16,padding:'10px',borderRadius:10,border:'1.5px solid #e2e8f0',background:'transparent',color:'#64748b',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Parent Exams & Marks (combined) ──────────────────────────────
function ParentExams({ db, child }) {
  const sid = child.sid || child.id;
  const [now, setNow] = useState(new Date());
  const [activeTab, setActiveTab] = useState('schedule');

  // Refresh every 30 seconds so status updates in real-time
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const today = now.toISOString().split('T')[0];
  const exams = (db.exams || []).filter(e => !e.cls || e.cls === child.cls);

  const fmtTime = t => { if (!t) return null; const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`; };

  const effectiveStatus = e => {
    if (e.dt && e.startTime && e.endTime) {
      const start = new Date(e.dt + 'T' + e.startTime);
      const end = new Date(e.dt + 'T' + e.endTime);
      if (!isNaN(start) && !isNaN(end)) {
        if (now >= start && now <= end) return 'Ongoing';
        if (now > end) return 'Completed';
        return 'Upcoming';
      }
    } else if (e.dt) {
      const day = new Date(e.dt); day.setHours(23, 59, 59);
      return now > day ? 'Completed' : 'Upcoming';
    }
    return e.st || 'Upcoming';
  };

  const ongoing  = exams.filter(e => effectiveStatus(e) === 'Ongoing');
  const upcoming = exams.filter(e => effectiveStatus(e) === 'Upcoming').sort((a,b) => (a.dt||'').localeCompare(b.dt||''));
  const past     = exams.filter(e => effectiveStatus(e) === 'Completed').sort((a,b) => (b.dt||'').localeCompare(a.dt||''));

  const statusStyle = { Ongoing: { bg: '#fef3c7', c: '#d97706' }, Upcoming: { bg: '#ede9fe', c: '#7c3aed' }, Completed: { bg: '#dcfce7', c: '#059669' } };

  const ExamRow = ({ e }) => {
    const st = effectiveStatus(e);
    const ss = statusStyle[st] || statusStyle.Upcoming;
    const timeStr = e.startTime && e.endTime
      ? `${fmtTime(e.startTime)} – ${fmtTime(e.endTime)}`
      : e.startTime ? fmtTime(e.startTime) : null;
    // Student's mark for this exam
    const eid = e.eid || e.id;
    const marksKey = e.cls ? `${eid}_${e.cls}` : eid;
    const mark = (db.marks?.[marksKey] || {})[sid];
    const pct = mark !== undefined ? Math.round(mark / (e.max || 100) * 100) : null;
    const markCol = pct !== null ? (pct >= 75 ? '#059669' : pct >= 50 ? '#d97706' : '#dc2626') : null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: ss.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: ss.c }}>quiz</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{e.name}{e.su ? ` — ${e.su}` : ''}</div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {e.dt || ''}
            {timeStr ? ` · ${timeStr}` : ''}
            {e.max ? ` · Max: ${e.max}` : ''}
          </div>
        </div>
        {mark !== undefined && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: markCol }}>{mark}/{e.max || 100}</div>
            <div style={{ fontSize: 10, color: markCol }}>{pct}%</div>
          </div>
        )}
        <span style={{ padding: '3px 10px', borderRadius: 20, background: ss.bg, color: ss.c, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{st}</span>
      </div>
    );
  };

  // Marks results
  const results = [];
  (db.exams || []).forEach(e => {
    const eid = e.eid || e.id;
    const marksKey = e.cls ? `${eid}_${e.cls}` : eid;
    const mark = (db.marks?.[marksKey] || {})[sid];
    if (mark !== undefined && mark !== null) {
      const pct = Math.round(mark / (e.max || 100) * 100);
      const g = pct >= 91 ? 'A1' : pct >= 81 ? 'A2' : pct >= 71 ? 'B1' : pct >= 61 ? 'B2' : pct >= 51 ? 'C1' : pct >= 41 ? 'C2' : pct >= 33 ? 'D' : 'E';
      results.push({ name: e.name, su: e.su, dt: e.dt, marks: mark, max: e.max || 100, pct, g });
    }
  });
  results.sort((a, b) => (b.dt || '').localeCompare(a.dt || ''));

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 900, color: '#1e293b', fontFamily: 'Manrope,sans-serif', marginBottom: 20 }}>Exams & Marks</h1>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', padding: 4, borderRadius: 12, width: 'fit-content', marginBottom: 20 }}>
        {[['schedule', 'Schedule'], ['marks', 'My Marks']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={{ padding: '8px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 150ms',
              background: activeTab === id ? '#fff' : 'transparent', color: activeTab === id ? '#1960a3' : '#64748b',
              boxShadow: activeTab === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'schedule' && (
        <>
          {ongoing.length > 0 && (
            <div style={{ background: '#fffbeb', borderRadius: 16, padding: '20px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)', marginBottom: 16, border: '1.5px solid #fcd34d' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#d97706', marginBottom: 14 }}>🔴 Ongoing Now</div>
              {ongoing.map(e => <ExamRow key={e.id} e={e} />)}
            </div>
          )}
          <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 14 }}>Upcoming Exams</div>
            {upcoming.length === 0 ? <div style={{ fontSize: 12, color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>No upcoming exams</div>
              : upcoming.map(e => <ExamRow key={e.id} e={e} />)}
          </div>
          <div style={{ background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 14 }}>Past Exams</div>
            {past.length === 0 ? <div style={{ fontSize: 12, color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>No past exams</div>
              : past.map(e => <ExamRow key={e.id} e={e} />)}
          </div>
        </>
      )}

      {activeTab === 'marks' && (
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 8px rgba(0,31,77,0.06)', overflow: 'hidden' }}>
          {results.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No results available yet</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Exam', 'Subject', 'Date', 'Marks', 'Grade'].map(h => (
                  <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', background: '#f8fafc' }}>{h}</th>
                ))}</tr></thead>
                <tbody>{results.map((r, i) => {
                  const col = r.pct >= 75 ? '#059669' : r.pct >= 50 ? '#d97706' : '#dc2626';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 20px', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{r.name}</td>
                      <td style={{ padding: '12px 20px', fontSize: 12, color: '#64748b' }}>{r.su || '—'}</td>
                      <td style={{ padding: '12px 20px', fontSize: 12, color: '#64748b' }}>{r.dt || '—'}</td>
                      <td style={{ padding: '12px 20px', fontSize: 13, fontWeight: 800, color: col }}>{r.marks}/{r.max}</td>
                      <td style={{ padding: '12px 20px' }}><span style={{ padding: '3px 10px', borderRadius: 20, background: col + '18', color: col, fontSize: 11, fontWeight: 800 }}>{r.g}</span></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Parent Calendar ───────────────────────────────────────────────
function ParentCalendar({ db, child, activeSessionId }) {
  const now = new Date();
  const [viewYear, setViewYear] = React.useState(now.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(now.getMonth());
  const [selectedDay, setSelectedDay] = React.useState(null);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const MONTHS_S = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const WDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const today = now.getDate(), todayM = now.getMonth(), todayY = now.getFullYear();

  const INDIA_HOLIDAYS_FULL = [
    {dt:'2024-01-14',title:'Makar Sankranti / Pongal',type:'holiday',note:'Harvest festival'},{dt:'2024-01-22',title:'Ram Mandir Pran Pratishtha',type:'holiday',note:'Ayodhya consecration'},{dt:'2024-01-26',title:'Republic Day',type:'holiday',note:'National Holiday'},
    {dt:'2024-02-14',title:"Valentine's Day",type:'event',note:''},{dt:'2024-03-08',title:'Maha Shivaratri',type:'holiday',note:'Hindu festival'},{dt:'2024-03-25',title:'Holi',type:'holiday',note:'Festival of colours'},
    {dt:'2024-03-29',title:'Good Friday',type:'holiday',note:'Christian holiday'},{dt:'2024-04-09',title:'Ugadi / Gudi Padwa',type:'holiday',note:'New Year festival'},{dt:'2024-04-11',title:'Id-ul-Fitr (Eid)',type:'holiday',note:'End of Ramadan'},
    {dt:'2024-04-14',title:'Dr. Ambedkar Jayanti',type:'holiday',note:'National holiday'},{dt:'2024-04-17',title:'Ram Navami',type:'holiday',note:'Hindu festival'},{dt:'2024-04-21',title:'Mahavir Jayanti',type:'holiday',note:'Jain festival'},
    {dt:'2024-05-23',title:'Buddha Purnima',type:'holiday',note:'Buddhist festival'},{dt:'2024-06-17',title:'Eid ul-Adha (Bakrid)',type:'holiday',note:'Islamic festival'},{dt:'2024-07-17',title:'Muharram',type:'holiday',note:'Islamic New Year'},
    {dt:'2024-08-15',title:'Independence Day',type:'holiday',note:'National Holiday'},{dt:'2024-08-26',title:'Janmashtami',type:'holiday',note:"Krishna's birthday"},{dt:'2024-09-07',title:'Ganesh Chaturthi',type:'holiday',note:'Hindu festival'},
    {dt:'2024-09-16',title:'Milad-un-Nabi',type:'holiday',note:"Prophet's birthday"},{dt:'2024-10-02',title:'Gandhi Jayanti',type:'holiday',note:'National Holiday'},{dt:'2024-10-02',title:'Navratri begins',type:'event',note:'9-day festival'},
    {dt:'2024-10-12',title:'Dussehra (Vijayadashami)',type:'holiday',note:'Hindu festival'},{dt:'2024-10-31',title:'Halloween',type:'event',note:''},{dt:'2024-11-01',title:'Diwali',type:'holiday',note:'Festival of lights'},
    {dt:'2024-11-02',title:'Govardhan Puja',type:'holiday',note:'Hindu festival'},{dt:'2024-11-03',title:'Bhai Dooj',type:'holiday',note:'Hindu festival'},{dt:'2024-11-15',title:'Guru Nanak Jayanti',type:'holiday',note:'Sikh festival'},
    {dt:'2024-12-25',title:'Christmas Day',type:'holiday',note:'Christian holiday'},{dt:'2024-12-31',title:"New Year's Eve",type:'event',note:''},
    {dt:'2025-01-01',title:"New Year's Day",type:'holiday',note:''},{dt:'2025-01-14',title:'Makar Sankranti / Pongal',type:'holiday',note:'Harvest festival'},{dt:'2025-01-23',title:'Netaji Subhas Chandra Bose Jayanti',type:'holiday',note:''},
    {dt:'2025-01-26',title:'Republic Day',type:'holiday',note:'National Holiday'},{dt:'2025-02-02',title:'Basant Panchami',type:'holiday',note:'Saraswati Puja'},{dt:'2025-02-12',title:'Maha Shivaratri',type:'holiday',note:'Hindu festival'},
    {dt:'2025-02-14',title:"Valentine's Day",type:'event',note:''},{dt:'2025-02-26',title:'Holi (Holika Dahan)',type:'holiday',note:'Eve of Holi'},{dt:'2025-03-14',title:'Holi',type:'holiday',note:'Festival of colours'},
    {dt:'2025-03-30',title:'Ugadi / Gudi Padwa',type:'holiday',note:'New Year festival'},{dt:'2025-03-31',title:'Id-ul-Fitr (Eid)',type:'holiday',note:'End of Ramadan'},{dt:'2025-04-06',title:'Ram Navami',type:'holiday',note:'Hindu festival'},
    {dt:'2025-04-10',title:'Mahavir Jayanti',type:'holiday',note:'Jain festival'},{dt:'2025-04-14',title:'Dr. Ambedkar Jayanti',type:'holiday',note:'National holiday'},{dt:'2025-04-14',title:'Baisakhi / Tamil New Year',type:'holiday',note:'Harvest festival'},
    {dt:'2025-04-18',title:'Good Friday',type:'holiday',note:'Christian holiday'},{dt:'2025-04-20',title:'Easter Sunday',type:'holiday',note:'Christian holiday'},{dt:'2025-05-12',title:'Buddha Purnima',type:'holiday',note:'Buddhist festival'},
    {dt:'2025-06-07',title:'Eid ul-Adha (Bakrid)',type:'holiday',note:'Islamic festival'},{dt:'2025-06-27',title:'Muharram',type:'holiday',note:'Islamic New Year'},{dt:'2025-07-10',title:'Guru Purnima',type:'event',note:''},
    {dt:'2025-08-09',title:'Raksha Bandhan',type:'holiday',note:'Hindu festival'},{dt:'2025-08-15',title:'Independence Day',type:'holiday',note:'National Holiday'},{dt:'2025-08-16',title:'Janmashtami',type:'holiday',note:"Krishna's birthday"},
    {dt:'2025-08-27',title:'Ganesh Chaturthi',type:'holiday',note:'Hindu festival'},{dt:'2025-09-05',title:'Milad-un-Nabi',type:'holiday',note:"Prophet's birthday"},{dt:'2025-09-05',title:"Teachers' Day",type:'event',note:"Dr. Radhakrishnan's birthday"},
    {dt:'2025-10-02',title:'Gandhi Jayanti',type:'holiday',note:'National Holiday'},{dt:'2025-10-02',title:'Navratri begins',type:'event',note:'9-day festival'},{dt:'2025-10-02',title:'Dussehra (Vijayadashami)',type:'holiday',note:'Hindu festival'},
    {dt:'2025-10-20',title:'Diwali',type:'holiday',note:'Festival of lights'},{dt:'2025-10-21',title:'Govardhan Puja',type:'holiday',note:'Hindu festival'},{dt:'2025-10-22',title:'Bhai Dooj',type:'holiday',note:'Hindu festival'},
    {dt:'2025-10-31',title:'Halloween',type:'event',note:''},{dt:'2025-11-05',title:'Guru Nanak Jayanti',type:'holiday',note:'Sikh festival'},{dt:'2025-11-14',title:"Children's Day",type:'event',note:"Nehru's birthday"},
    {dt:'2025-12-25',title:'Christmas Day',type:'holiday',note:'Christian holiday'},{dt:'2025-12-31',title:"New Year's Eve",type:'event',note:''},
    {dt:'2026-01-01',title:"New Year's Day",type:'holiday',note:''},{dt:'2026-01-14',title:'Makar Sankranti / Pongal',type:'holiday',note:'Harvest festival'},{dt:'2026-01-26',title:'Republic Day',type:'holiday',note:'National Holiday'},
    {dt:'2026-02-01',title:'Maha Shivaratri',type:'holiday',note:'Hindu festival'},{dt:'2026-02-14',title:"Valentine's Day",type:'event',note:''},{dt:'2026-03-03',title:'Holi',type:'holiday',note:'Festival of colours'},
    {dt:'2026-03-19',title:'Ugadi / Gudi Padwa',type:'holiday',note:'New Year festival'},{dt:'2026-03-20',title:'Id-ul-Fitr (Eid)',type:'holiday',note:'End of Ramadan'},{dt:'2026-03-26',title:'Ram Navami',type:'holiday',note:'Hindu festival'},
    {dt:'2026-03-31',title:'Mahavir Jayanti',type:'holiday',note:'Jain festival'},{dt:'2026-04-03',title:'Good Friday',type:'holiday',note:'Christian holiday'},{dt:'2026-04-05',title:'Easter Sunday',type:'holiday',note:'Christian holiday'},
    {dt:'2026-04-14',title:'Dr. Ambedkar Jayanti',type:'holiday',note:'National holiday'},{dt:'2026-04-14',title:'Baisakhi',type:'holiday',note:'Harvest festival'},{dt:'2026-05-01',title:'Buddha Purnima',type:'holiday',note:'Buddhist festival'},
    {dt:'2026-05-27',title:'Eid ul-Adha (Bakrid)',type:'holiday',note:'Islamic festival'},{dt:'2026-08-15',title:'Independence Day',type:'holiday',note:'National Holiday'},{dt:'2026-09-05',title:"Teachers' Day",type:'event',note:"Dr. Radhakrishnan's birthday"},
    {dt:'2026-10-02',title:'Gandhi Jayanti',type:'holiday',note:'National Holiday'},{dt:'2026-11-08',title:'Diwali',type:'holiday',note:'Festival of lights'},{dt:'2026-11-14',title:"Children's Day",type:'event',note:"Nehru's birthday"},
    {dt:'2026-12-25',title:'Christmas Day',type:'holiday',note:'Christian holiday'},
    {dt:'2027-01-01',title:"New Year's Day",type:'holiday',note:''},{dt:'2027-01-26',title:'Republic Day',type:'holiday',note:'National Holiday'},{dt:'2027-08-15',title:'Independence Day',type:'holiday',note:'National Holiday'},
    {dt:'2027-10-02',title:'Gandhi Jayanti',type:'holiday',note:'National Holiday'},{dt:'2027-12-25',title:'Christmas Day',type:'holiday',note:'Christian holiday'},
  ];

  const allEvents = [
    ...INDIA_HOLIDAYS_FULL.map(h => ({ ...h, id: 'IH_' + h.dt + '_' + h.title.slice(0,8) })),
    ...(db.events || []),
    ...db.exams.filter(e => e.dt).map(e => ({ id: e.id, title: e.name + (e.su ? ' – ' + e.su : ''), dt: e.dt, type: 'exam', cls: e.cls, note: e.cls ? 'Class: ' + e.cls : '' })),
  ];

  const offset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

  const monthEvents = allEvents.filter(e => { const d = new Date(e.dt); return d.getFullYear() === viewYear && d.getMonth() === viewMonth; });
  const eventsByDay = {};
  monthEvents.forEach(e => { const d = new Date(e.dt).getDate(); if (!eventsByDay[d]) eventsByDay[d] = []; eventsByDay[d].push(e); });

  const upcoming = allEvents.filter(e => new Date(e.dt) >= new Date(now.toDateString())).sort((a,b) => new Date(a.dt)-new Date(b.dt)).slice(0,8);
  const isToday = d => d === today && viewMonth === todayM && viewYear === todayY;

  const typeStyle = type => {
    if (type === 'exam') return { bg: 'bg-error-container/30', text: 'text-on-error-container', dot: 'bg-error', label: 'Examination', icon: 'quiz' };
    if (type === 'holiday') return { bg: 'bg-[#ffdeaa]/50', text: 'text-[#5f4100]', dot: 'bg-[#f8bc4b]', label: 'Holiday', icon: 'celebration' };
    if (type === 'event') return { bg: 'bg-secondary-container/20', text: 'text-on-secondary-container', dot: 'bg-secondary', label: 'Event', icon: 'event' };
    return { bg: 'bg-primary/5', text: 'text-primary', dot: 'bg-primary', label: 'Academic', icon: 'school' };
  };

  const fmtDate = dt => {
    const d = new Date(dt);
    return { day: d.getDate(), mon: MONTHS_S[d.getMonth()], dow: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()] };
  };

  const openDay = (d, evs) => {
    const dtStr = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    setSelectedDay({ d, evs, dtStr });
  };

  return (
    <div className="space-y-0 -m-6">
      {/* Header */}
      <div className="px-10 pt-10 pb-8 flex justify-between items-end flex-wrap gap-4">
        <div>
          <p className="text-primary font-bold tracking-[0.15em] text-xs uppercase mb-2">Academic Calendar</p>
          <h1 className="text-5xl font-extrabold text-on-surface tracking-tight leading-none mb-2 font-headline">{MONTHS[viewMonth]} {viewYear}</h1>
          <p className="text-on-surface-variant text-base">Academic events, examinations, and public holidays at a glance.</p>
        </div>
        <div className="flex flex-col gap-3 items-end">
          <div className="flex items-center gap-2 bg-surface-container rounded-xl p-1 shadow-sm">
            <button onClick={() => setViewYear(y => y-1)} className="px-3 py-2 rounded-lg text-slate-500 hover:text-primary hover:bg-surface-container-lowest transition-all border-0 bg-transparent cursor-pointer">
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            <span className="px-3 text-sm font-black text-primary min-w-[52px] text-center">{viewYear}</span>
            <button onClick={() => setViewYear(y => y+1)} className="px-3 py-2 rounded-lg text-slate-500 hover:text-primary hover:bg-surface-container-lowest transition-all border-0 bg-transparent cursor-pointer">
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>
          <div className="flex flex-wrap gap-1 justify-end max-w-sm">
            {MONTHS_S.map((m, i) => (
              <button key={i} onClick={() => setViewMonth(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border-0 cursor-pointer ${viewMonth===i ? 'bg-primary text-white shadow-sm' : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-lowest hover:text-primary'}`}>
                {m}
              </button>
            ))}
            <button onClick={() => { setViewMonth(todayM); setViewYear(todayY); }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-surface-container-high text-primary hover:bg-surface-container-lowest transition-all border-0 cursor-pointer">
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="px-10 pb-10 grid grid-cols-12 gap-8">
        {/* Main Calendar */}
        <div className="col-span-12 xl:col-span-9 bg-surface-container-low rounded-3xl p-8 shadow-sm">
          <div className="grid grid-cols-7 mb-4">
            {WDAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-black tracking-widest text-on-surface-variant uppercase py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({length: offset}).map((_,i) => (
              <div key={'p'+i} className="aspect-square p-2 rounded-2xl opacity-20 bg-surface-container-high/50 flex flex-col">
                <span className="text-xs font-bold text-slate-400">{daysInPrev - offset + i + 1}</span>
              </div>
            ))}
            {Array.from({length: daysInMonth}).map((_,i) => {
              const d = i+1;
              const evs = eventsByDay[d] || [];
              const isWknd = [0,6].includes(new Date(viewYear, viewMonth, d).getDay());
              const todayCell = isToday(d);
              const domEv = evs[0];
              const ts = domEv ? typeStyle(domEv.type) : null;
              return (
                <div key={d} onClick={() => openDay(d, evs)}
                  className={`aspect-square p-2.5 rounded-2xl flex flex-col justify-between cursor-pointer transition-all hover:scale-[1.03] hover:shadow-md
                    ${todayCell ? 'bg-primary-container ring-4 ring-primary/15 shadow-lg' : ts ? ts.bg : isWknd ? 'bg-surface-container' : 'bg-surface-container-lowest shadow-sm hover:ring-2 hover:ring-primary/10'}`}>
                  <span className={`text-sm font-black leading-none ${todayCell ? 'text-white' : ts ? ts.text : isWknd ? 'text-slate-400' : 'text-on-surface'}`}>
                    {String(d).padStart(2,'0')}
                  </span>
                  <div className="space-y-0.5">
                    {todayCell && <span className="block text-[8px] font-bold uppercase tracking-widest text-white/70">Today</span>}
                    {evs.slice(0,1).map(ev => (
                      <span key={ev.id} className={`block text-[8px] font-bold leading-tight truncate ${todayCell ? 'text-white/90' : ts?.text}`}>{ev.title}</span>
                    ))}
                    {evs.length > 1 && <span className={`block text-[8px] font-bold ${todayCell ? 'text-white/60' : (ts?.text || '') + ' opacity-60'}`}>+{evs.length-1} more</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="col-span-12 xl:col-span-3 flex flex-col gap-5">
          {selectedDay ? (
            <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm border-l-4 border-primary">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant">{MONTHS[viewMonth]} {viewYear}</p>
                  <p className="text-3xl font-extrabold text-primary font-headline">{String(selectedDay.d).padStart(2,'0')}</p>
                  <p className="text-xs text-on-surface-variant font-medium">
                    {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date(viewYear, viewMonth, selectedDay.d).getDay()]}
                  </p>
                </div>
                <button onClick={() => setSelectedDay(null)} className="p-1.5 rounded-lg hover:bg-surface-container text-on-surface-variant transition-colors border-0 bg-transparent cursor-pointer">
                  <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
              </div>
              {selectedDay.evs.length ? (
                <div className="space-y-3">
                  {selectedDay.evs.map(ev => {
                    const ts = typeStyle(ev.type);
                    return (
                      <div key={ev.id} className={`p-3 rounded-xl ${ts.bg}`}>
                        <div className="flex items-start gap-2">
                          <span className={`material-symbols-outlined text-[16px] mt-0.5 ${ts.text}`}>{ts.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold leading-tight ${ts.text}`}>{ev.title}</p>
                            {ev.note && <p className="text-[10px] text-on-surface-variant mt-0.5">{ev.note}</p>}
                            {ev.cls && <p className="text-[10px] text-on-surface-variant">Class: {ev.cls}</p>}
                            <span className={`inline-block mt-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${ts.bg} ${ts.text} border border-current/20`}>{ts.label}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <span className="material-symbols-outlined text-2xl text-on-surface-variant opacity-30 block mb-1">event_available</span>
                  <p className="text-xs text-on-surface-variant">No events on this day</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm">
              <h3 className="text-xs font-black text-primary uppercase tracking-widest mb-5">Calendar Key</h3>
              <div className="space-y-3 mb-5">
                {[{type:'academic',label:'Academic Events'},{type:'exam',label:'Examinations'},{type:'holiday',label:'Public Holidays'},{type:'event',label:'Events & Festivals'}].map(({type,label}) => {
                  const ts = typeStyle(type);
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${ts.bg} border-2 ${ts.text} border-current`}></div>
                      <span className="text-sm font-medium text-on-surface">{label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-surface-container-high pt-4">
                <div className="flex items-center gap-1.5 text-[10px] text-on-surface-variant mb-1">
                  <span className="material-symbols-outlined text-[12px]">touch_app</span>
                  Click any date to see details
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-on-surface-variant">
                  <span className="material-symbols-outlined text-[12px]">public</span>
                  Indian public holidays included
                </div>
              </div>
            </div>
          )}

          <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm">
            <h3 className="text-xs font-black text-primary uppercase tracking-widest mb-3">This Month</h3>
            <div className="space-y-2">
              {['holiday','exam','event','academic'].map(t => {
                const cnt = monthEvents.filter(e => e.type === t).length;
                if (!cnt) return null;
                const ts = typeStyle(t);
                return (
                  <div key={t} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${ts.dot}`}></div>
                      <span className="text-on-surface-variant">{ts.label}</span>
                    </div>
                    <span className="font-bold text-primary">{cnt}</span>
                  </div>
                );
              })}
              {!monthEvents.length && <p className="text-xs text-on-surface-variant">No events this month</p>}
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-extrabold font-headline text-on-surface mb-4">Upcoming</h3>
            <div className="space-y-3">
              {upcoming.map(ev => {
                const { day, mon, dow } = fmtDate(ev.dt);
                const ts = typeStyle(ev.type);
                return (
                  <div key={ev.id+ev.dt}
                    onClick={() => { const d = new Date(ev.dt); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); openDay(d.getDate(), allEvents.filter(e => e.dt === ev.dt)); }}
                    className="bg-surface-container-lowest p-4 rounded-2xl flex gap-3 items-start shadow-sm hover:translate-x-1 transition-transform cursor-pointer group">
                    <div className={`flex flex-col items-center justify-center min-w-[46px] py-2 rounded-xl bg-surface-container-low transition-colors`}>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{mon}</span>
                      <span className="text-xl font-black text-primary leading-none">{day}</span>
                      <span className="text-[9px] text-slate-400 font-medium">{dow}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-on-surface leading-tight mb-1 truncate">{ev.title}</p>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${ts.dot}`}></div>
                        <span className="text-[10px] text-on-surface-variant font-medium">{ts.label}{ev.cls ? ' • '+ev.cls : ''}</span>
                      </div>
                      {ev.note && <p className="text-[10px] text-on-surface-variant mt-0.5 truncate">{ev.note}</p>}
                    </div>
                  </div>
                );
              })}
              {!upcoming.length && (
                <div className="text-center py-8 text-on-surface-variant">
                  <span className="material-symbols-outlined text-3xl block mb-2 opacity-30">event_available</span>
                  <p className="text-xs font-medium">No upcoming events</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Parent Messages ───────────────────────────────────────────────
function ParentMessages({ db, child, student, activeSessionId }) {
  const [msgs, setMsgs] = useState([]);
  const [form, setForm] = useState({ type: 'message', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!activeSessionId) return;
    getMessages(activeSessionId).then(all => {
      setMsgs(all.filter(m => m.studentSid === (child.sid || child.id)));
    }).catch(() => {});
  }, [activeSessionId, child.sid, child.id]);

  const doSend = async () => {
    if (!form.subject.trim()) return;
    setSending(true);
    try {
      const msg = await sendMessage(activeSessionId, {
        studentSid: child.sid || child.id,
        studentName: (child.fn || '') + ' ' + (child.ln || ''),
        cls: child.cls,
        type: form.type,
        subject: form.subject.trim(),
        body: form.body.trim(),
      });
      setMsgs(prev => [msg, ...prev]);
      setForm({ type: 'message', subject: '', body: '' });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (e) {
      alert('Failed to send: ' + e.message);
    }
    setSending(false);
  };

  const TYPE_OPTS = [
    { v: 'message', label: 'General Message' },
    { v: 'certificate', label: 'Certificate Request' },
    { v: 'request', label: 'Other Request' },
  ];

  const statusColor = { unread: { bg: '#dbeafe', c: '#1960a3' }, read: { bg: '#f1f5f9', c: '#64748b' }, resolved: { bg: '#dcfce7', c: '#059669' } };

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 900, color: '#1e293b', fontFamily: 'Manrope,sans-serif', marginBottom: 24 }}>Messages & Requests</h1>

      {/* Compose */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '24px', boxShadow: '0 1px 8px rgba(0,31,77,0.06)', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 16 }}>Send a Message to Admin</div>
        {sent && <div style={{ background: '#dcfce7', color: '#059669', borderRadius: 10, padding: '10px 14px', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>Message sent successfully!</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Type</div>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', background: '#fff' }}>
              {TYPE_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Subject *</div>
            <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Request for Transfer Certificate"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Message (optional)</div>
          <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={3} placeholder="Add details here…"
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </div>
        <button onClick={doSend} disabled={sending || !form.subject.trim()}
          style={{ padding: '11px 28px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#002045,#1960a3)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.7 : 1 }}>
          {sending ? 'Sending…' : 'Send Message'}
        </button>
      </div>

      {/* Sent messages */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 8px rgba(0,31,77,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Sent Messages ({msgs.length})</div>
        {msgs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No messages sent yet</div>
        ) : msgs.map((m, i) => {
          const sc = statusColor[m.status] || statusColor.unread;
          return (
            <div key={m._id || i} style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{m.subject}</div>
                <span style={{ padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.c, fontSize: 10, fontWeight: 700, textTransform: 'capitalize' }}>{m.status}</span>
              </div>
              {m.body && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{m.body}</div>}
              <div style={{ fontSize: 10, color: '#94a3b8' }}>{new Date(m.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {m.type}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
